import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import FullscreenIcon from "@mui/icons-material/OpenInFull";
import FullscreenExitIcon from "@mui/icons-material/CloseFullscreen";
import CloseIcon from "@mui/icons-material/Close";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DockIcon from "@mui/icons-material/VerticalSplit";
import TuneIcon from "@mui/icons-material/Tune";
import ArticleIcon from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SubjectIcon from "@mui/icons-material/Subject";
import NearMeIcon from "@mui/icons-material/NearMe";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { navigateAndHighlight } from "./helpNavigation";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import ReactMarkdown from "react-markdown";
import { streamOpsAssistantAPI } from "../requests/utilities/streamingOpsAssistant";
import {
  API_ops_assistant_chat_stream,
  API_ops_assistant_shortcuts,
  API_ops_assistant_shortcut_templates,
  API_ops_assistant_transcribe_voice,
  API_update_assistant_interaction_feedback,
  API_setup_assistant_chat_stream,
  API_setup_assistant_confirm_action,
  API_setup_assistant_bulk_confirm,
  API_get_onboarding_summary,
} from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import { handleOnboardingAction } from "../onboarding/handleOnboardingAction";
import SetupChecklistCard from "./SetupChecklistCard";
import { buildChecklistItems, completedBetween, isChecklistRequest } from "./setupChecklist";
import {
  followAlongTargetForTask,
  followAlongTargetForTools,
  followAlongViewportAllows,
  isSameFollowAlongTarget,
} from "./setupFollowAlong";
import { ONBOARDING_UPDATED_EVENT, setOnboardingIntent } from "../onboarding/onboardingNavigation";
import {
  SETUP_ASSISTANT_NAME,
  buildSetupGreeting,
  buildSetupFarewell,
  buildAnswerChips,
  buildStarterChips,
  getRandomSetupThinkingLine,
  isSetupComplete,
  onlyRealStoreId,
  nextActionableStepAfter,
  welcomeStepStatus,
  deferSkipActionFor,
} from "./setupAssistantCopy";
import WidgetRenderer from "../reporting/charts/WidgetRenderer";
import ActionCard from "./ActionCard";
import PosCredentialCard from "./PosCredentialCard";
import RosterUploadCard from "./RosterUploadCard";
import RosterProposalCard from "./RosterProposalCard";
import RolesProposalCard from "./RolesProposalCard";
import LocationCreateProposalCard from "./LocationCreateProposalCard";
import SetupBulkActionCard from "./SetupBulkActionCard";
import LocationSettingsProposalCard from "./LocationSettingsProposalCard";
import BulkActionCard from "./BulkActionCard";
import MessageProposalCard from "./MessageProposalCard";
import LogbookProposalCard from "./LogbookProposalCard";
import DayNoteProposalCard from "./DayNoteProposalCard";
import TrainingInputProposalCard from "./TrainingInputProposalCard";
import TrainingCheckpointProposalCard from "./TrainingCheckpointProposalCard";
import FormGradeProposalCard from "./FormGradeProposalCard";
import SaveReportProposalCard from "./SaveReportProposalCard";
import InviteProposalCard from "./InviteProposalCard";
import TicketProposalCard from "./TicketProposalCard";
import TicketApprovalProposalCard from "./TicketApprovalProposalCard";
import TimeOffProposalCard from "./TimeOffProposalCard";
import AvailabilityProposalCard from "./AvailabilityProposalCard";
import SupportRequestProposalCard from "./SupportRequestProposalCard";
import ManageShortcutsModal from "./ManageShortcutsModal";

// Natural-language list join: ["a","b","c"] -> "a, b, and c".
const formatCapabilityList = (items) => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

// Only advertise capabilities the store actually has enabled — otherwise a
// scheduling-off (training-first) store gets greeted with "approvals,
// scheduling, daily recap" for things Rovis can't do there. Flags follow the
// app convention: undefined/null = enabled, only an explicit false disables.
const buildRovisGreetings = ({ schedulingEnabled, trainingEnabled } = {}) => {
  const caps = [];
  if (schedulingEnabled !== false) caps.push("approvals", "scheduling");
  if (trainingEnabled !== false) caps.push("training");
  caps.push("metrics", "your team");
  const list = formatCapabilityList(caps);
  return [
    `Hi! I'm Rovis, your operations assistant. I can help with ${list}, all from right here.`,
    "Hey there, Rovis reporting for duty ✨. What can I help you with today?",
    `Welcome back! I'm Rovis. Ask me about ${list}, or a quick performance snapshot.`,
  ];
};

// How close to the bottom the reader must be for transcript growth to keep
// them pinned there.
const NEAR_BOTTOM_PX = 160;

const ROVIS_THINKING_LINES = [
  "Rovis is on it...",
  "Rovis is taking a look...",
  "Rovis is checking now...",
  "Rovis is pulling that together...",
  "Rovis is reviewing the details...",
  "Rovis is getting that ready...",
];

const buildInitialMessage = (greetingFlags) => {
  const greetings = buildRovisGreetings(greetingFlags);
  return {
    id: Date.now(),
    content: greetings[Math.floor(Math.random() * greetings.length)],
    sender: "ai",
    timestamp: new Date(),
    chartData: null,
    proposals: null,
    sources: [],
    showSuggestions: true,
  };
};

// Setup mode (Lumi) greeting: same message shape as buildInitialMessage so
// every render path downstream treats it identically.
// Until the checklist has loaded, the greeting is a placeholder rather than
// a generic line that would be swapped out a moment later.
const buildSetupInitialMessage = (summary) => ({
  id: Date.now(),
  content: summary ? buildSetupGreeting(summary) : "",
  loading: !summary,
  sender: "ai",
  timestamp: new Date(),
  chartData: null,
  proposals: null,
  sources: [],
  showSuggestions: true,
});

// Convert backend annotations (from _resolve_rag_annotations) into the
// FE source shape rendered by the source footer. Same mapping the
// knowledge assistant (AssistantDisplay.js) uses, kept identical so
// future tweaks land in one place.
const formatAnnotationsAsSources = (annotations) =>
  (annotations || []).map((annotation) => ({
    id: annotation.uuid || Math.random().toString(),
    title: annotation.name || "Unknown document",
    path:
      (annotation.type === "FileUpload"
        ? annotation.presigned_url
        : annotation.pdf) || "/",
    extension: annotation.extension || "",
    fileSize: annotation.fileSize || "",
    type: annotation.type || "file",
  }));

const getSafeSourceHref = (path) => {
  if (!path || typeof path !== "string") return "#";

  try {
    const url = new URL(path, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.href : "#";
  } catch (error) {
    return "#";
  }
};

// Pass through the backend-provided file size string. Both serializers
// (FileUpload.file_size_display, LearningSection.pdf_size_display)
// already format bytes as human-readable strings (e.g. "12.50 KB",
// "1.50 MB"). Coercing with Number(...) here would NaN those strings
// and silently hide sizes — matches the knowledge assistant's
// AssistantDisplay.formatFileSize, kept identical so the citation UI
// stays in sync between both surfaces.
const formatFileSize = (size) => (size ? size : "");

const getRandomThinkingLine = () =>
  ROVIS_THINKING_LINES[Math.floor(Math.random() * ROVIS_THINKING_LINES.length)];

export default function OperationsAssistantPanel({
  homeStore,
  homeCompany,
  storeNames,
  setCurrentTab,
  username,
  permissionRole,
  badgeCounts,
  panelState,
  setPanelState,
  onActionConfirmed,
  onDockChange,
  // Incremented by the parent when the other assistant panel takes the dock.
  undockSignal = 0,
  fabPosition,
  scheduleViewingWeek,
  schedulingEnabled,
  trainingEnabled,
  // "operations" (Rovis, store-scoped) or "setup" (Lumi, company-scoped
  // Setup Assistant). Every setup-specific branch is guarded on isSetupMode so
  // operations mode behaves exactly as before.
  mode = "operations",
  change_home_store,
  onSetupComplete,
  // Setup mode: opens Rovis from the closing card; null when Rovis is not
  // available to this user (the parent decides).
  onOpenOperationsAssistant = null,
  // Setup mode: bumped by the parent when onboarding progress may have changed
  // outside this panel (Onboarding Center actions, a location added), so the
  // summary is re-read and the panel can come back after hiding at 100%.
  setupSummaryVersion = 0,
}) {
  const isSetupMode = mode === "setup";
  // Capabilities the greeting may advertise. Recomputed each render so the
  // initial message and the New-chat reset reflect the store's current flags.
  const greetingFlags = { schedulingEnabled, trainingEnabled };
  // Setup mode: the onboarding summary drives the greeting, the starter chips,
  // and the hide-at-100% behavior. It is expensive, so it is fetched once per
  // company and after confirmed setup actions, never per turn.
  const [setupSummary, setSetupSummary] = useState(null);
  const setupSummaryRef = useRef(setupSummary);
  setupSummaryRef.current = setupSummary;
  const setupComplete = isSetupMode && isSetupComplete(setupSummary);
  const buildGreetingMessage = () =>
    isSetupMode
      ? buildSetupInitialMessage(setupSummaryRef.current)
      : buildInitialMessage(greetingFlags);
  // Conversations are store-scoped for Rovis and company-scoped for Lumi. A
  // scope prop can be empty for a render while the app reloads store data
  // (a new location becoming the home store); that is not a scope change, so
  // the last real key is kept and the conversation survives.
  const rawScopeKey = isSetupMode ? homeCompany : homeStore;
  const lastScopeKeyRef = useRef(rawScopeKey ?? null);
  if (rawScopeKey != null) lastScopeKeyRef.current = rawScopeKey;
  const sessionScopeKey = rawScopeKey ?? lastScopeKeyRef.current;
  // Setup mode: the parsed roster from the upload card, sent with each turn
  // as roster_upload until the import is confirmed or dismissed.
  const [setupRoster, setSetupRoster] = useState(null);
  // A follow-up turn queued by a card (credential connected, roster uploaded)
  // that is sent once streaming ends; cleared with the conversation.
  const [pendingSetupFollowUp, setPendingSetupFollowUp] = useState(null);
  // True from the moment a setup card resolves until Lumi's next-step turn
  // starts streaming. The chip row would otherwise flash in that window:
  // the card is no longer pending, but the summary refresh, the checklist
  // card, and the 400 ms delay all run before isStreaming hides it.
  const [nextStepPending, setNextStepPending] = useState(false);
  // A conversation "generation" bumps on company switch and on Reset, so an
  // upload that finishes after either is discarded instead of resurrected.
  const conversationGenRef = useRef(0);
  const nextStepTimerRef = useRef(null);
  const nextStepSentRef = useRef(false);
  // Bumps on every visible user message: a confirmation's refreshes that
  // finish after the user has taken the turn must not queue a next-step turn
  // behind their reply.
  const userTurnSeqRef = useRef(0);
  // Set when an import card was confirmed with welcome messages turned off:
  // holds { storeId } for the roster's own location (recorded at confirm
  // time, since a batch's last-resolved card can belong to another store).
  const welcomesDeclinedRef = useRef(null);
  // The step a deferred next-step turn engaged instead of the checklist's
  // own next best action, so the turn's done does not re-engage the latter.
  const engagedOverrideRef = useRef(null);
  // Held while a checklist turn is cancelling cards, so a second Enter in
  // that window cannot start a turn ahead of the checklist.
  const checklistBusyRef = useRef(false);
  // Setup mode "follow along": the app opens the section where each step
  // lands so the user can watch Lumi work. Always on in setup mode; the
  // navigation itself is wired below setupActionOptions.
  const followAlongNavRef = useRef(() => {});
  // Title of the last confirmed card in the current batch, so that cancelling
  // the batch's remaining cards can still hand the turn back to Lumi.
  const batchConfirmedTitleRef = useRef(null);
  // The summary the last in-chat checklist was built from, so the next one
  // can highlight what finished since.
  const checklistShownForRef = useRef(null);
  // The step Lumi is already working on (its chip was clicked, or she opened
  // it herself after a confirm): its chip is redundant until the step moves.
  const [engagedStepKey, setEngagedStepKey] = useState(null);
  const currentStepChipKey = (summary) =>
    (buildStarterChips(summary).find((chip) => chip.taskKey && !chip.kind) || {}).taskKey || null;
  const lastFollowTargetRef = useRef(null);
  const messagesRef = useRef([]);
  const conversationGenAtRender = conversationGenRef.current;
  const [messages, setMessages] = useState(() => [buildGreetingMessage()]);
  messagesRef.current = messages;
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  // True while a checklist turn is rejecting cards: the whole thread goes
  // inert so a Confirm cannot race the reject that is already in flight.
  const [cardsAreCancelling, setCardsAreCancelling] = useState(false);
  const [thinkingText, setThinkingText] = useState("");
  const [sessionUuid, setSessionUuid] = useState(null);
  const [sessionStoreId, setSessionStoreId] = useState(homeStore);

  // User-customized shortcut chips + the Rightwork template library
  const [shortcuts, setShortcuts] = useState([]);
  const [shortcutTemplates, setShortcutTemplates] = useState([]);
  const [isManageOpen, setIsManageOpen] = useState(false);

  // Dock state
  const [docked, setDocked] = useState(false);

  // Per-message expanded-state for the knowledge-source footer chip.
  // Keyed by message id; absent = collapsed (default).
  const [expandedSources, setExpandedSources] = useState({});
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const toggleSources = (messageId) =>
    setExpandedSources((prev) => ({ ...prev, [messageId]: !prev[messageId] }));

  // Push-to-talk voice input. voiceState: "idle" → "recording" → "transcribing".
  // mediaRecorderRef holds the MediaRecorder; mediaStreamRef holds the
  // underlying MediaStream so we can release the mic when we stop. Chunks
  // accumulate in audioChunksRef and get assembled into a Blob on stop.
  const [voiceState, setVoiceState] = useState("idle");
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(0);
  // axios CancelToken source for the transcription POST. (Named "abort"
  // for parity with the mobile AbortController; the repo is pinned to
  // axios 0.21.4 which doesn't honor AbortController signals, so we use
  // the legacy CancelToken API here.) Held in a ref so the teardown paths
  // (cancel button, panel close, store change, unmount) can cancel an
  // in-flight upload without setInputValue firing on a hidden panel.
  const transcribeAbortRef = useRef(null);
  // Set when a successful transcription needs the textarea focused once
  // voiceState transitions back to "idle" (the textarea is disabled while
  // transcribing, so focus() inside the onstop handler is a no-op).
  const pendingFocusRef = useRef(false);
  // Identifies the currently-pending startRecording attempt. Set to a fresh
  // token at the top of startRecording and checked after getUserMedia
  // resolves; teardown nulls it. If the token doesn't survive the await we
  // know teardown happened mid-startup (panel close, reset, store change,
  // unmount) — we stop the freshly-acquired stream so the mic doesn't leak
  // into a hidden/reset panel.
  const pendingStartTokenRef = useRef(null);
  // Same idea for the stop pipeline. recorder.stop() is sync but the onstop
  // handler runs async, so teardown can fire in the interleave window;
  // without a token, the onstop closure would install a fresh AbortController
  // and POST the captured audio onto a torn-down panel.
  const pendingStopTokenRef = useRef(null);

  // Drag state
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const dragStartOffset = useRef({ x: 0, y: 0 });

  // Resize state
  const [panelSize, setPanelSize] = useState({ w: 420, h: 550 });
  const isResizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ w: 420, h: 550 });

  const fabPositionRef = useRef(fabPosition);
  fabPositionRef.current = fabPosition;

  const panelSizeRef = useRef(panelSize);
  panelSizeRef.current = panelSize;

  const dragOffsetRef = useRef(dragOffset);
  dragOffsetRef.current = dragOffset;

  // Notify parent of dock state and width changes
  useEffect(() => {
    onDockChange?.(docked ? panelSize.w : 0);
    return () => onDockChange?.(0);
  }, [docked, panelSize.w, onDockChange]);

  // The other panel took the dock: float this one so they never overlap.
  useEffect(() => {
    if (undockSignal > 0) setDocked(false);
  }, [undockSignal]);

  // Reset position and undock on close
  useEffect(() => {
    if (panelState === "closed") {
      if (docked) setDocked(false);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [panelState, docked]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesContentRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const panelRootRef = useRef(null);

  // Reset conversation when store changes, abort any in-flight stream
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Also tear down any voice activity (live recording, in-flight upload,
    // pending start awaiting permission prompt). Without this, a manager
    // who switches stores while startRecording is awaiting getUserMedia
    // lands a live mic on the new store; once setSessionStoreId(homeStore)
    // below runs, the voice-aware effects can't detect the switch via
    // sessionStoreId !== homeStore anymore. teardownRecording also nulls
    // pendingStartTokenRef so any in-flight startup bails on its next guard.
    teardownRecording();
    setVoiceState("idle");
    // Setup mode: the summary still belongs to the previous company until
    // refreshSetupSummary resolves, so build the greeting without it rather
    // than showing the old company's next step.
    if (isSetupMode) setupSummaryRef.current = null;
    setMessages([buildGreetingMessage()]);
    setSessionUuid(null);
    setSessionStoreId(homeStore);
    setIsStreaming(false);
    setThinkingText("");
    // Drop per-message expanded-source state — its keys reference the
    // old message ids, which won't exist in the new conversation.
    setExpandedSources({});
    // An uploaded roster belongs to the previous company's conversation and
    // must never ride along into another company's turns.
    setSetupRoster(null);
    setPendingSetupFollowUp(null);
    setNextStepPending(false);
    if (nextStepTimerRef.current) { clearTimeout(nextStepTimerRef.current); nextStepTimerRef.current = null; }
    // Company-scoped follow-along state must not carry over to the next
    // company: its chips, screen, checklist diff, and next-step guard start fresh.
    setEngagedStepKey(null);
    lastFollowTargetRef.current = null;
    checklistShownForRef.current = null;
    nextStepSentRef.current = false;
    welcomesDeclinedRef.current = null;
    engagedOverrideRef.current = null;
    conversationGenRef.current += 1;
  }, [sessionScopeKey]);

  // Setup mode: load the onboarding summary. A failed fetch must not block
  // chat, so the greeting simply keeps its generic line.
  const setupSummaryRequestRef = useRef(0);
  const refreshSetupSummary = useCallback(async () => {
    if (!isSetupMode || !homeCompany) return;
    const requestSequence = ++setupSummaryRequestRef.current;
    try {
      const res = await getRightworkAPI(API_get_onboarding_summary, {
        company_id: homeCompany,
      });
      if (requestSequence !== setupSummaryRequestRef.current) return;
      if (res?.status === 200) {
        const summary = res?.data?.redux?.payload || null;
        setSetupSummary(summary);
        // Callers that await this refresh read the ref right away, before
        // the re-render that would otherwise update it.
        setupSummaryRef.current = summary;
        // The first summary is the baseline the first in-chat checklist diffs against.
        if (checklistShownForRef.current === null) checklistShownForRef.current = summary;
        // Refresh the greeting while the conversation has not started yet so
        // the "Next up" line reflects the latest checklist state.
        setMessages((prev) =>
          prev.length === 1 && prev[0].sender === "ai"
            ? [{ ...prev[0], content: buildSetupGreeting(summary), loading: false }]
            : prev
        );
      }
    } catch (error) {
      console.error("Failed to load the onboarding summary for the setup assistant", error);
      // No checklist: fall back to the generic greeting rather than a blank.
      setMessages((prev) =>
        prev.length === 1 && prev[0].sender === "ai" && prev[0].loading
          ? [{ ...prev[0], content: buildSetupGreeting(null), loading: false }]
          : prev
      );
    }
  }, [isSetupMode, homeCompany]);

  useEffect(() => {
    if (!isSetupMode) return;
    setSetupSummary(null);
    refreshSetupSummary();
  }, [isSetupMode, homeCompany, refreshSetupSummary]);

  useEffect(() => {
    if (!isSetupMode || setupSummaryVersion === 0) return;
    refreshSetupSummary();
  }, [isSetupMode, setupSummaryVersion, refreshSetupSummary]);

  useEffect(() => {
    if (!isSetupMode) return;
    const refreshAfterSave = (event) => {
      if (String(event.detail?.companyId) === String(homeCompany)) refreshSetupSummary();
    };
    window.addEventListener(ONBOARDING_UPDATED_EVENT, refreshAfterSave);
    return () => window.removeEventListener(ONBOARDING_UPDATED_EVENT, refreshAfterSave);
  }, [isSetupMode, homeCompany, refreshSetupSummary]);

  // At 100% the panel shows a closing card; once it is dismissed (or Rovis
  // is opened from it) the panel hides and the parent hides the FAB. Both
  // come back if progress drops below 100%. Undock first so the hidden
  // panel stops reserving dock width.
  // The dismissal is remembered per company in this browser, so the card is
  // not shown again on every new session or remount after it was closed.
  const farewellStorageKey = isSetupMode && homeCompany
    ? `rw-setup-farewell-dismissed:${homeCompany}`
    : null;
  const readFarewellDismissed = () => {
    if (!farewellStorageKey) return false;
    try {
      return localStorage.getItem(farewellStorageKey) === "1";
    } catch (_) {
      return false;
    }
  };
  const [farewellDismissed, setFarewellDismissedState] = useState(readFarewellDismissed);
  // An admin who switches companies without remounting the panel gets that
  // company's own dismissal, not the previous one's.
  useEffect(() => {
    let dismissed = false;
    if (farewellStorageKey) {
      try {
        dismissed = localStorage.getItem(farewellStorageKey) === "1";
      } catch (_) {}
    }
    setFarewellDismissedState(dismissed);
  }, [farewellStorageKey]);
  const setFarewellDismissed = useCallback((dismissed) => {
    setFarewellDismissedState(dismissed);
    if (!farewellStorageKey) return;
    try {
      if (dismissed) localStorage.setItem(farewellStorageKey, "1");
      else localStorage.removeItem(farewellStorageKey);
    } catch (_) {}
  }, [farewellStorageKey]);
  useEffect(() => {
    if (!isSetupMode) return;
    if (!setupComplete) {
      // Progress dropped (an undo): the card is due again when it completes.
      // Only a summary for THIS company may clear its saved dismissal; right
      // after a company switch the summary still belongs to the previous one.
      const summaryCompany = setupSummary?.company?.id;
      if (setupSummary && (summaryCompany == null || String(summaryCompany) === String(homeCompany))) {
        setFarewellDismissed(false);
      }
      onSetupComplete?.(false);
      return;
    }
    if (docked) setDocked(false);
    if (farewellDismissed) onSetupComplete?.(true);
  }, [isSetupMode, setupComplete, setupSummary, docked, farewellDismissed, onSetupComplete, setFarewellDismissed]);

  // "docked" is a parent-requested open state (setup mode auto-open after
  // company creation): open already docked to the left rail, then normalize
  // to "default" so the rest of the panel state machine is unchanged.
  useEffect(() => {
    if (panelState === "docked") {
      setDocked(true);
      setPanelSize((prev) => ({ ...prev, w: Math.min(prev.w, 360) }));
      setDragOffset({ x: 0, y: 0 });
      setPanelState("default");
    }
  }, [panelState, setPanelState]);

  // Load the user's shortcuts + the template library once. Run them
  // independently so one failure (e.g. shortcuts table not migrated yet)
  // doesn't also drop the templates list.
  useEffect(() => {
    // Setup mode has no shortcuts; starter chips come from setupAssistantCopy.
    if (isSetupMode) return undefined;
    let cancelled = false;
    axios
      .get(API_ops_assistant_shortcuts, getDefaultAxiosConfig())
      .then((res) => {
        if (!cancelled) setShortcuts(res.data?.shortcuts || []);
      })
      .catch((err) => {
        console.error("Failed to load ops assistant shortcuts", err);
      });
    axios
      .get(API_ops_assistant_shortcut_templates, {
        ...getDefaultAxiosConfig(),
        params: { store_id: homeStore },
      })
      .then((res) => {
        if (!cancelled) setShortcutTemplates(res.data?.templates || []);
      })
      .catch((err) => {
        console.error("Failed to load ops assistant shortcut templates", err);
      });
    return () => {
      cancelled = true;
    };
    // Re-fetch on store switch so the template filter reflects the active
    // store's scheduling_enabled (templates are store-scoped; shortcuts are
    // user-scoped and simply re-load harmlessly).
  }, [homeStore, isSetupMode]);

  // Auto-scroll. The chip row renders only after streaming ends (and, in
  // setup mode, once the engaged step / next-step gate settle), which is
  // after the last message update, so those must scroll too or the chips
  // land below the fold.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinkingText, isStreaming, engagedStepKey, nextStepPending]);
  // Anything that grows the transcript after that scroll (the chip row, a
  // card expanding, late layout) would otherwise leave the bottom out of
  // view. Pin to the bottom on growth, but only when the reader is already
  // there, so scrolling up to reread is never hijacked.
  useEffect(() => {
    const content = messagesContentRef.current;
    const container = messagesContainerRef.current;
    if (!content || !container || typeof ResizeObserver === "undefined") return undefined;
    // Whether the reader was at the bottom BEFORE the content grew. Measured
    // on scroll, not after the resize: a single growth larger than the
    // threshold (the roster card's form, say) would otherwise read as
    // "scrolled up" and leave the new bottom out of view.
    let nearBottom = true;
    const measure = () => {
      nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= NEAR_BOTTOM_PX;
    };
    // Initialize from the actual position: a panel-state change (expand,
    // dock) reruns this effect, and a reader who had scrolled up must not
    // be treated as at the bottom just because the observer reattached.
    measure();
    container.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(() => {
      if (!nearBottom) return;
      container.scrollTop = container.scrollHeight;
    });
    observer.observe(content);
    return () => {
      container.removeEventListener("scroll", measure);
      observer.disconnect();
    };
    // Re-attach whenever the panel opens: a closed panel renders no
    // transcript, so a mount-only effect would never observe anything.
  }, [panelState]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // Reset panel drag offset when opening so it anchors to current FAB position
  useEffect(() => {
    if (panelState === "default") {
      setDragOffset({ x: 0, y: 0 });
    }
  }, [panelState]);

  // Escape to minimize. Two instances (Rovis and Lumi) can be open at once,
  // so only close this one when focus is inside it, or when nothing has
  // focus (body/null), which preserves the single-panel behavior.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Escape" || panelState === "closed") return;
      const active = document.activeElement;
      const focusIsFree = !active || active === document.body;
      const focusIsInside = Boolean(
        active && panelRootRef.current && panelRootRef.current.contains(active)
      );
      if (focusIsFree || focusIsInside) {
        setPanelState("closed");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [panelState, setPanelState]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e) => {
      if (panelState === "expanded") return;
      if (docked) {
        // Calculate offset to keep panel at its docked position (left: 60px, top: 0)
        const panelW = panelSizeRef.current.w;
        const panelH = panelSizeRef.current.h;
        const baseRight = Math.max(0, Math.min(fabPositionRef.current.right, window.innerWidth - panelW));
        const baseBottom = Math.max(0, Math.min(fabPositionRef.current.bottom, window.innerHeight - panelH));
        const defaultLeft = window.innerWidth - baseRight - panelW;
        const defaultTop = window.innerHeight - baseBottom - panelH;
        const newOffset = { x: -(defaultLeft - 60), y: -defaultTop };
        setDragOffset(newOffset);
        dragStartOffset.current = newOffset;
        setDocked(false);
        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
        return;
      }
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      dragStartOffset.current = { ...dragOffset };
      e.preventDefault();
    },
    [panelState, dragOffset, docked]
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;

      const panelW = panelSizeRef.current.w;
      const panelH = panelSizeRef.current.h;
      // Default position is based on FAB: left = window.innerWidth - right - panelW, top = window.innerHeight - bottom - panelH
      const baseRight = Math.max(0, Math.min(fabPositionRef.current.right, window.innerWidth - panelW));
      const baseBottom = Math.max(0, Math.min(fabPositionRef.current.bottom, window.innerHeight - panelH));
      const defaultLeft = window.innerWidth - baseRight - panelW;
      const defaultTop = window.innerHeight - baseBottom - panelH;

      let newX = dragStartOffset.current.x + dx;
      let newY = dragStartOffset.current.y + dy;

      // Clamp so panel stays within viewport
      const minX = -defaultLeft;           // Can't go past left edge
      const maxX = baseRight;              // Can't go past right edge
      const minY = -defaultTop;            // Can't go past top edge
      const maxY = baseBottom;             // Can't go past bottom edge

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));

      setDragOffset({ x: newX, y: newY });
    };
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        // Snap to left dock if panel's left edge is near the left side
        const panelW = panelSizeRef.current.w;
        const baseRight = Math.max(0, Math.min(fabPositionRef.current.right, window.innerWidth - panelW));
        const defaultLeft = window.innerWidth - baseRight - panelW;
        const currentLeft = defaultLeft + (dragOffsetRef.current.x || 0);
        if (currentLeft <= 80) {
          setDocked(true);
          setPanelSize((prev) => ({ ...prev, w: Math.min(prev.w, 360) }));
          setDragOffset({ x: 0, y: 0 });
        }
      }
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Resize handlers
  const resizeEdge = useRef(null);
  const resizeStartDragOffset = useRef({ x: 0, y: 0 });

  const handleResizeMouseDown = useCallback(
    (edge) => (e) => {
      if (panelState === "expanded") return;
      isResizing.current = true;
      resizeEdge.current = edge;
      resizeStart.current = { x: e.clientX, y: e.clientY };
      resizeStartSize.current = { ...panelSize };
      resizeStartDragOffset.current = { ...dragOffset };
      e.preventDefault();
      e.stopPropagation();
    },
    [panelState, panelSize, dragOffset]
  );

  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!isResizing.current) return;
      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;
      const edge = resizeEdge.current;

      let newW = resizeStartSize.current.w;
      let newH = resizeStartSize.current.h;

      // Vertical: top edges drag up to expand, bottom edges drag down
      if (edge.includes("top")) {
        newH = resizeStartSize.current.h - dy;
      } else if (edge.includes("bottom")) {
        newH = resizeStartSize.current.h + dy;
      }
      // Horizontal: left edges drag left to expand, right edges drag right
      if (edge.includes("left")) {
        newW = resizeStartSize.current.w - dx;
      } else if (edge.includes("right")) {
        newW = resizeStartSize.current.w + dx;
      }

      newW = Math.max(320, Math.min(window.innerWidth, newW));
      newH = Math.max(400, Math.min(window.innerHeight, newH));

      // Offset position to keep the opposite edge pinned.
      // Panel uses CSS `bottom` + `right` positioning, so increasing height
      // naturally grows upward and increasing width grows leftward.
      // We only need to compensate when resizing from the bottom or right edges.
      const actualDw = newW - resizeStartSize.current.w;
      const actualDh = newH - resizeStartSize.current.h;
      let offsetX = resizeStartDragOffset.current.x;
      let offsetY = resizeStartDragOffset.current.y;
      if (edge.includes("bottom")) {
        offsetY = resizeStartDragOffset.current.y + actualDh;
      }
      if (edge.includes("right")) {
        offsetX = resizeStartDragOffset.current.x + actualDw;
      }

      // Clamp offset so panel stays within viewport at the new size
      const baseRight = Math.max(0, Math.min(fabPositionRef.current.right, window.innerWidth - newW));
      const baseBottom = Math.max(0, Math.min(fabPositionRef.current.bottom, window.innerHeight - newH));
      const defaultLeft = window.innerWidth - baseRight - newW;
      const defaultTop = window.innerHeight - baseBottom - newH;
      offsetX = Math.max(-defaultLeft, Math.min(baseRight, offsetX));
      offsetY = Math.max(-defaultTop, Math.min(baseBottom, offsetY));

      setPanelSize({ w: newW, h: newH });
      setDragOffset({ x: offsetX, y: offsetY });
    };
    const handleResizeUp = () => {
      isResizing.current = false;
      resizeEdge.current = null;
    };
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeUp);
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeUp);
    };
  }, []);

  // Build chip row: live badge-driven contextual chips first (when any are
  // present), then the user's saved shortcuts. Each chip carries its own
  // prompt so the label shown in-row can be terse while the LLM sees the
  // verbose instruction.
  const getSuggestions = () => {
    if (isSetupMode) {
      // No chips until the checklist is in (they would be generic and then swapped).
      if (!setupSummary) return [];
      const stepChips = buildStarterChips(setupSummary).filter(
        (chip) => chip.kind || !chip.taskKey || chip.taskKey !== engagedStepKey
      );
      // Quick answers for the step Lumi has just asked about.
      const lastMessage = messages[messages.length - 1];
      const answerChips =
        engagedStepKey && lastMessage?.sender === "ai" && !lastMessage.showSuggestions
          ? buildAnswerChips(engagedStepKey)
          : [];
      return [...answerChips, ...stepChips];
    }
    const suggestions = [];

    // Contextual chips driven by real-time pending-action counts
    if (badgeCounts?.time_off_requests_pending_count > 0) {
      const count = badgeCounts.time_off_requests_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Review ${count} pending time-off request${plural}`,
        prompt: `Show me the ${count} pending time-off request${plural} at my current store and help me decide how to respond to each.`,
      });
    }
    if (badgeCounts?.availability_requests_pending_count > 0) {
      const count = badgeCounts.availability_requests_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Review ${count} availability change request${plural}`,
        prompt: `Show me the ${count} pending availability change request${plural} at my current store.`,
      });
    }
    if (badgeCounts?.shift_trade_requests_pending_count > 0) {
      const count = badgeCounts.shift_trade_requests_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Review ${count} pending shift trade${plural}`,
        prompt: `Show me the ${count} pending shift trade request${plural} at my current store.`,
      });
    }
    if (badgeCounts?.shift_drop_requests_pending_count > 0) {
      const count = badgeCounts.shift_drop_requests_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        // The badge counts every pending drop, with or without pickup bids,
        // so the copy must not promise bids.
        label: `Review ${count} pending shift drop${plural}`,
        prompt: `Show me the ${count} pending shift drop request${plural} at my current store. For each shift, list any pickup bids so I can choose one to approve, and note which drops have no bids yet.`,
      });
    }
    if (badgeCounts?.open_shifts_pending_count > 0) {
      const count = badgeCounts.open_shifts_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Review ${count} upcoming open shift${plural} needing coverage`,
        // The badge counts published open shifts from now onward (any
        // future date), so the prompt must say so — a window-less prompt
        // makes the model check only today and contradict the count.
        prompt: `Show me the ${count} open shift${plural} in the shift pool awaiting coverage at my current store — published open shifts from today onward, any upcoming date. For each, list any pickup bids, and suggest replacement candidates where possible.`,
      });
    }
    if (badgeCounts?.training_actions_pending_count > 0) {
      const count = badgeCounts.training_actions_pending_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Review ${count} pending training action${plural}`,
        prompt: `Show me the ${count} pending training action${plural} at my current store — employees blocked on checkpoints, failed quizzes, or manager input — and what I need to do for each.`,
      });
    }
    if (badgeCounts?.forms_needing_grading_count > 0) {
      const count = badgeCounts.forms_needing_grading_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Grade ${count} form submission${plural} awaiting review`,
        prompt: `Show me the ${count} form submission${plural} at my current store awaiting a grade, and let me grade each one on the spot.`,
      });
    }
    if (badgeCounts?.tickets_awaiting_my_approval_count > 0) {
      const count = badgeCounts.tickets_awaiting_my_approval_count;
      const plural = count !== 1 ? "s" : "";
      suggestions.push({
        label: `Approve ${count} ticket${plural} awaiting you`,
        prompt: `Show me the ${count} ticket${plural} at my current store that are waiting for my approval, and let me approve or reject each one.`,
      });
    }

    // User-saved shortcuts, in display_order
    for (const s of shortcuts) {
      suggestions.push({
        label: s.label,
        prompt: s.prompt,
        shortcutId: s.id,
      });
    }

    return suggestions;
  };

  // Setup mode keeps the current-step chips under every reply (not only the
  // greeting) so the next step is always one tap away; they hide while a
  // card is waiting for a decision.
  // ...or while a credential form / team card is waiting: another POS chip
  // would start a second turn with the first form still live.
  const setupChipsAvailable =
    isSetupMode &&
    !nextStepPending &&
    !messages.some(
      (m) =>
        (m.proposals || []).some((p) => p.status === "pending") ||
        m.credentialRequest?.status === "pending" ||
        m.rosterUploadRequest?.status === "pending"
    );

  const handleSuggestionClick = async (suggestion) => {
    if (isStreaming) return;
    // Setup chips can carry a checklist action: "Skip this step" advances the
    // checklist and Lumi announces the new step herself; "Back to: <step>"
    // undoes the skip, then starts that step through Lumi.
    if (isSetupMode && suggestion.action) {
      // The checklist action reports false when the server refused it (for
      // example while a recommended setup run is active); nothing to announce then.
      const ok = await handleOnboardingAction(suggestion.action, setupActionOptions);
      if (ok === false) return;
      if (suggestion.kind === "skip" || suggestion.kind === "done") {
        nextStepSentRef.current = false;
        const verb = suggestion.kind === "done" ? "Reviewed" : "Skipped";
        await notifyActionConfirmed(["mark_setup_step"], `${verb}: ${suggestion.title || suggestion.taskKey}`);
        return;
      }
      await notifyActionConfirmed(["mark_setup_step"]);
    }
    if (isSetupMode && suggestion.kind === "defer" && suggestion.taskKey) {
      // Deferring the step (invites: "Not yet"): record the checklist's own
      // skip when the step offers one, so the deferral is server-side state
      // that survives reloads and keeps the starter chips off the step.
      const summary = setupSummaryRef.current;
      const deferStoreId = summary?.nextBestAction?.store_id || null;
      const skip = deferSkipActionFor(summary, suggestion.taskKey, deferStoreId);
      if (skip.action) {
        const ok = await handleOnboardingAction(skip.action, setupActionOptions);
        if (ok === false) return;
        nextStepSentRef.current = false;
        await notifyActionConfirmed(["mark_setup_step"], `Skipped: ${skip.title}`);
        return;
      }
      // No skip on this step: engage the next actionable step locally.
      welcomesDeclinedRef.current =
        suggestion.taskKey === "welcome_message_sent" ? { storeId: deferStoreId } : null;
      const after = nextActionableStepAfter(summary, suggestion.taskKey, deferStoreId);
      setEngagedStepKey(after?.key || null);
      if (after?.key) {
        const target = followAlongTargetForTask(after.key, after.store_id || onlyRealStoreId(summary));
        if (target) followAlongNavRef.current(target);
      }
      const prompt = after?.title ? `${suggestion.prompt} The next step is: ${after.title}.` : suggestion.prompt;
      sendMessage(prompt, { displayLabel: suggestion.label });
      return;
    }
    if (isSetupMode && suggestion.taskKey && !suggestion.kind) {
      setEngagedStepKey(suggestion.taskKey);
      // Open the step's page behind the chat as Lumi starts it, on the
      // step's own location (or the only real one), so the user never has
      // to ask to be taken there.
      const target = followAlongTargetForTask(
        suggestion.taskKey,
        suggestion.storeId || onlyRealStoreId(setupSummaryRef.current)
      );
      if (target) followAlongNavRef.current(target);
    }
    if (suggestion.prompt) sendMessage(suggestion.prompt, { displayLabel: suggestion.label });
  };

  // Build conversation history (last 10 messages, skip greeting)
  const getConversationHistoryFrom = (msgs) => {
    const history = msgs
      .filter((m) => m.sender === "user" || m.sender === "ai" || m.sender === "checklist")
      .slice(1)
      .slice(-10)
      .flatMap((m) => {
        // A checklist was answered here, not by Lumi, but it did answer the
        // user's question: without it the next turn reads as two user turns
        // in a row with the request still open.
        const entry = m.sender === "checklist"
          ? { role: "assistant", content: "[Showed the setup checklist]" }
          : { role: m.sender === "user" ? "user" : "assistant", content: m.content };

        // Append action outcomes so the LLM knows what was confirmed/rejected
        if (m.sender === "ai" && m.proposals?.length > 0) {
          const resolved = m.proposals.filter(
            (p) => p.status === "confirmed" || p.status === "cancelled" || p.status === "rejected"
          );
          if (resolved.length > 0) {
            const lines = resolved.map((p) => {
              const s = p.summary || {};
              const parts = [s.title, s.employee_name, s.dates || s.date, s.type].filter(Boolean);
              const label = parts.join(" — ");
              if (p.status === "confirmed") return `[Confirmed: ${label}]`;
              return `[Cancelled: ${label}]`;
            });
            return [entry, { role: "user", content: "Action outcomes: " + lines.join(" | ") }];
          }
        }
        // Setup mode: tell the LLM how a credential card resolved. Only the
        // outcome is sent; credential values never enter messages state.
        if (isSetupMode && m.sender === "ai" && m.credentialRequest) {
          const cred = m.credentialRequest;
          const label =
            (cred.title || "").replace(/^Connect\s+/i, "") || cred.provider || "POS";
          if (cred.status === "connected") {
            return [entry, { role: "user", content: `[Connected: ${label}]` }];
          }
          if (cred.status === "dismissed") {
            return [entry, { role: "user", content: `[Skipped: ${label} credentials]` }];
          }
        }
        // Setup mode: how a roster upload card resolved. Only counts are
        // sent; the rows travel on the roster_upload request param.
        if (isSetupMode && m.sender === "ai" && m.rosterUploadRequest) {
          const roster = m.rosterUploadRequest;
          if (roster.status === "uploaded") {
            const count = roster.uploaded_count ?? 0;
            return [entry, { role: "user", content: `[Roster uploaded: ${roster.file_name || "roster"}, ${count} people]` }];
          }
          if (roster.status === "dismissed") {
            return [entry, { role: "user", content: "[Skipped: roster upload]" }];
          }
        }
        return [entry];
      });
    return history.length > 0 ? history : undefined;
  };

  const sendMessage = async (text, { displayLabel, hidden = false } = {}) => {
    if (!text || isStreaming || checklistBusyRef.current) return;

    // "Show me the checklist" is answered right here with the checklist card.
    if (isSetupMode && !hidden && isChecklistRequest(text)) {
      const items = buildChecklistItems(setupSummaryRef.current);
      if (items.length > 0) {
        // Same cleanup a normal turn does: pending cards are cancelled and a
        // queued next-step turn is dropped, so nothing fires behind this.
        nextStepSentRef.current = false;
        batchConfirmedTitleRef.current = null;
        if (nextStepTimerRef.current) { clearTimeout(nextStepTimerRef.current); nextStepTimerRef.current = null; }
        setPendingSetupFollowUp((pending) => (pending && pending.hidden ? null : pending));
        // The server cancels pending proposals on an ordinary turn; this one
        // never reaches it, so reject them here or they stay confirmable.
        const stillPending = messages.flatMap((msg) =>
          (msg.proposals || []).filter((p) => p.status === "pending").map((p) => p.proposal_id)
        );
        // Awaited before the checklist replaces the cards, so a reload in
        // the middle cannot leave a card cancelled here and live there. One
        // at a time: each reject rewrites the session's whole proposal list.
        const cancelled = new Set(sessionUuid ? [] : stillPending);
        // A reset or company switch mid-flight makes this checklist stale.
        const generation = conversationGenRef.current;
        checklistBusyRef.current = true;
        setIsStreaming(true);
        setCardsAreCancelling(true);
        try {
          // One request per batch, not per card: a thread with many pending
          // cards would otherwise run into the per-minute limit and leave
          // some of them live.
          const batches = [];
          for (let start = 0; sessionUuid && start < stillPending.length; start += 100) {
            batches.push(stillPending.slice(start, start + 100));
          }
          for (const batch of batches) {
            if (generation !== conversationGenRef.current) break;
            try {
              const res = await postRightworkAPI(API_setup_assistant_bulk_confirm, {
                company_id: homeCompany,
                session_uuid: sessionUuid,
                actions: batch.map((proposalId) => ({ proposal_id: proposalId, action: "reject" })),
              });
              if (res?.status !== 200) continue;
              (res?.data?.redux?.payload?.results || []).forEach((row) => {
                if (!row?.result?.error) cancelled.add(row.proposal_id);
              });
            } catch (err) {
              // Still pending on the server, so it stays live here too.
            }
          }
        } finally {
          // Always cleared: a reset conversation must not inherit a frozen
          // composer or an inert message pane from the thread it replaced.
          checklistBusyRef.current = false;
          setIsStreaming(false);
          setCardsAreCancelling(false);
        }
        // The thread this checklist belongs to is gone.
        if (generation !== conversationGenRef.current) return;
        setMessages((prev) => [
          ...prev.map((msg) =>
            msg.proposals?.some((p) => p.status === "pending" && cancelled.has(p.proposal_id))
              ? { ...msg, proposals: msg.proposals.map((p) => (p.status === "pending" && cancelled.has(p.proposal_id) ? { ...p, status: "cancelled" } : p)) }
              : msg
          ),
          { id: Date.now(), content: text, sender: "user", timestamp: new Date(), displayLabel: displayLabel || null },
          { id: `checklist-${Date.now()}`, sender: "checklist", items, highlightKeys: [], timestamp: new Date() },
        ]);
        checklistShownForRef.current = setupSummaryRef.current;
        setInputValue("");
        return;
      }
    }

    const userMessage = {
      id: Date.now(),
      content: text,
      sender: "user",
      timestamp: new Date(),
      // When the message came from a shortcut chip, render the short label
      // in the user bubble instead of the verbose prompt the LLM sees.
      displayLabel: displayLabel || null,
      // Setup mode hands the turn back to Lumi after a confirmed card with a
      // synthetic user turn; it reaches the model but never shows as a bubble.
      hidden,
    };

    // Auto-cancel any pending proposals and build history before updating state
    const messagesWithCancelled = messages.map((msg) => {
      if (!msg.proposals?.some((p) => p.status === "pending")) return msg;
      return {
        ...msg,
        proposals: msg.proposals.map((p) =>
          p.status === "pending" ? { ...p, status: "cancelled" } : p
        ),
      };
    });
    setMessages([...messagesWithCancelled, userMessage]);
    if (!hidden) {
      // The user took the turn: a next-step turn still queued from the last
      // confirm must not fire behind their reply and cancel its cards.
      userTurnSeqRef.current += 1;
      nextStepSentRef.current = false;
      batchConfirmedTitleRef.current = null;
      if (nextStepTimerRef.current) { clearTimeout(nextStepTimerRef.current); nextStepTimerRef.current = null; }
      // The cancelled follow-up will never flip this back; without the reset
      // the chip row would stay hidden for the rest of the conversation.
      setNextStepPending(false);
      setPendingSetupFollowUp((pending) => (pending && pending.hidden ? null : pending));
    }
    setIsStreaming(true);
    setThinkingText(isSetupMode ? getRandomSetupThinkingLine() : getRandomThinkingLine());

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const aiMessageId = Date.now() + 1;
    let receivedTokens = false;
    let chartData = null;
    let collectedProposals = null;
    let credentialRequest = null;
    let rosterUploadRequest = null;

    const conversationHistory = getConversationHistoryFrom(messagesWithCancelled);

    try {
      await streamOpsAssistantAPI(
        isSetupMode ? API_setup_assistant_chat_stream : API_ops_assistant_chat_stream,
        {
          ...(isSetupMode
            ? { company_id: homeCompany, client: "web_setup_assistant" }
            : { store_id: homeStore }),
          user_query: text,
          ...(sessionUuid && { session_uuid: sessionUuid }),
          ...(conversationHistory && {
            conversation_history: conversationHistory,
          }),
          ...(!isSetupMode && scheduleViewingWeek && {
            schedule_viewing_week: scheduleViewingWeek,
          }),
          // Setup mode: the parsed roster rides along until the import card
          // is confirmed or dismissed, so the resolver can read it each turn.
          ...(isSetupMode && setupRoster && { roster_upload: setupRoster }),
          // Setup mode: lets Lumi say where the change shows up on screen.
          ...(isSetupMode && followAlongViewportAllows() && { follow_along: true }),
        },
        {
          onSessionInfo: (info) => {
            setSessionUuid(info.session_uuid);
            setSessionStoreId(homeStore);
          },
          onThinking: (thinkText) => {
            setThinkingText(thinkText);
          },
          onChartData: (data) => {
            chartData = data;
          },
          onActionProposal: (proposal) => {
            collectedProposals = [proposal];
          },
          onBulkActionProposal: (proposals) => {
            collectedProposals = proposals;
          },
          onCredentialRequest: (request) => {
            if (isSetupMode) credentialRequest = request;
          },
          onRosterUploadRequest: (request) => {
            if (isSetupMode) rosterUploadRequest = request;
          },
          onToken: (tokenText) => {
            // Any token clears the thinking spinner so it doesn't double-render
            // alongside the in-flight assistant message. A later thinking event
            // (e.g. between iterations of the tool loop) can re-show the spinner;
            // the next token clears it again.
            setThinkingText("");
            if (!receivedTokens) {
              receivedTokens = true;
              setMessages((prev) => [
                ...prev,
                {
                  id: aiMessageId,
                  content: tokenText,
                  sender: "ai",
                  timestamp: new Date(),
                  chartData: null,
                  proposals: null,
                  sources: [],
                  interactionKey: null,
                  interactionLogUuid: null,
                  feedbackRating: null,
                  feedbackSaving: false,
                  supportFollowUpStatus: null,
                },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId
                    ? { ...msg, content: msg.content + tokenText }
                    : msg
                )
              );
            }
          },
          onDone: (donePayload) => {
            // A hidden next-step turn means Lumi is now on the current step;
            // re-sync in case the checklist refreshed after the turn was queued.
            if (isSetupMode && hidden) {
              setEngagedStepKey(engagedOverrideRef.current || currentStepChipKey(setupSummaryRef.current));
              engagedOverrideRef.current = null;
            }
            const sources = formatAnnotationsAsSources(donePayload.annotations);
            // Product-help navigation payloads ({topic_id, label, tab,
            // anchor}) — rendered as "Show me" buttons under the reply.
            const helpNavs = Array.isArray(donePayload.help_navigations)
              ? donePayload.help_navigations
              : [];
            // Setup mode: Onboarding Center actions ({task_key, label,
            // store_id, action}) rendered as pills under the reply.
            const setupActions =
              isSetupMode && Array.isArray(donePayload.setup_actions)
                ? donePayload.setup_actions
                : [];
            // Setup mode: secure POS credential card (spec only, no values).
            const resolvedCredentialRequest = isSetupMode
              ? donePayload.credential_request || credentialRequest
              : null;
            const credentialRequestState = resolvedCredentialRequest
              ? { ...resolvedCredentialRequest, status: "pending" }
              : null;
            // Setup mode: roster upload card (spec only, no file contents).
            const resolvedRosterRequest = isSetupMode
              ? donePayload.roster_upload_request || rosterUploadRequest
              : null;
            const rosterUploadRequestState = resolvedRosterRequest
              ? { ...resolvedRosterRequest, status: "pending" }
              : null;
            // A turn that produced new cards starts a new batch: confirming
            // them must trigger the next-step turn even when no user message
            // came in between (Lumi chains steps herself).
            if (isSetupMode && Array.isArray(collectedProposals) && collectedProposals.length > 0) {
              nextStepSentRef.current = false;
            }
            // Follow along: open the section this turn's cards land on.
            if (isSetupMode) {
              const turnProposals = Array.isArray(collectedProposals) ? collectedProposals : [];
              const proposalTools = turnProposals.map((p) => p?.tool_name);
              const proposalStoreId = turnProposals.find((p) => p?.store_id)?.store_id;
              const target =
                followAlongTargetForTools(proposalTools, proposalStoreId) ||
                (resolvedCredentialRequest ? followAlongTargetForTask("company_pos_connected") : null) ||
                (resolvedRosterRequest
                  ? followAlongTargetForTask("employees_added", resolvedRosterRequest.store_id)
                  : null);
              if (target) followAlongNavRef.current(target);
            }
            if (receivedTokens) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        content: donePayload.text || msg.content,
                        chartData: chartData || donePayload.chart_data,
                        proposals: collectedProposals,
                        sources,
                        helpNavs,
                        setupActions,
                        credentialRequest: credentialRequestState,
                        rosterUploadRequest: rosterUploadRequestState,
                        interactionKey: donePayload.interaction_key || null,
                        interactionLogUuid: donePayload.interaction_log_uuid || null,
                        feedbackRating: null,
                        feedbackSaving: false,
                        supportFollowUpStatus: null,
                      }
                    : msg
                )
              );
            } else {
              setMessages((prev) => [
                ...prev,
                {
                  id: aiMessageId,
                  content: donePayload.text || "",
                  sender: "ai",
                  timestamp: new Date(),
                  chartData: chartData || donePayload.chart_data,
                  proposals: collectedProposals,
                  sources,
                  helpNavs,
                  setupActions,
                  credentialRequest: credentialRequestState,
                  rosterUploadRequest: rosterUploadRequestState,
                  interactionKey: donePayload.interaction_key || null,
                  interactionLogUuid: donePayload.interaction_log_uuid || null,
                  feedbackRating: null,
                  feedbackSaving: false,
                  supportFollowUpStatus: null,
                },
              ]);
            }
            setIsStreaming(false);
            setThinkingText("");
          },
          onError: (errorMsg) => {
            setMessages((prev) => [
              ...prev,
              {
                id: aiMessageId,
                content: errorMsg || "Something went wrong. Please try again.",
                sender: "system",
                timestamp: new Date(),
              },
            ]);
            setIsStreaming(false);
            setThinkingText("");
          },
        },
        { signal: abortController.signal }
      );
    } catch (err) {
      // Ignore abort errors from store change
      if (err.name === "AbortError") return;
      if (!receivedTokens) {
        setMessages((prev) => [
          ...prev,
          {
            id: aiMessageId,
            content: "Sorry, I couldn't process your request. Please try again.",
            sender: "system",
            timestamp: new Date(),
          },
        ]);
      }
      setIsStreaming(false);
      setThinkingText("");
    }
  };

  const handleAssistantFeedback = useCallback(async (messageId, rating) => {
    const target = messages.find((message) => message.id === messageId);
    if (!target || (!target.interactionLogUuid && !target.interactionKey)) return;

    const previousRating = target.feedbackRating;
    const nextRating = previousRating === rating ? null : rating;
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              feedbackRating: nextRating,
              feedbackSaving: true,
              supportFollowUpStatus:
                nextRating === "thumbs_down"
                  ? (message.supportFollowUpStatus || "suggested")
                  : (!nextRating && message.supportFollowUpStatus === "suggested")
                  ? "not_requested"
                  : (message.supportFollowUpStatus === "suggested" ? "not_requested" : message.supportFollowUpStatus),
            }
          : message
      )
    );

    try {
      const res = await postRightworkAPI(API_update_assistant_interaction_feedback, {
        interaction_uuid: target.interactionLogUuid,
        interaction_key: target.interactionKey,
        feedback_rating: nextRating || "clear",
      });
      const interaction = res?.data?.redux?.payload?.interaction || {};
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedbackSaving: false,
                feedbackRating: interaction.feedback_rating || null,
                supportFollowUpStatus: interaction.support_follow_up_status || message.supportFollowUpStatus,
              }
            : message
        )
      );
    } catch (error) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedbackSaving: false,
                feedbackRating: previousRating || null,
              }
            : message
        )
      );
    }
  }, [messages]);

  const handleCopyAssistantMessage = useCallback(async (messageId, content) => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      window.setTimeout(() => {
        setCopiedMessageId((current) => (current === messageId ? null : current));
      }, 1200);
    } catch (error) {
      setCopiedMessageId(null);
    }
  }, []);

  const handleAssistantSupportRequest = useCallback(async (messageId) => {
    const target = messages.find((message) => message.id === messageId);
    if (!target || (!target.interactionLogUuid && !target.interactionKey)) return;

    const previousStatus = target.supportFollowUpStatus;
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              feedbackRating: "thumbs_down",
              supportFollowUpStatus: "requested",
              supportFollowUpSaving: true,
            }
          : message
      )
    );

    try {
      const res = await postRightworkAPI(API_update_assistant_interaction_feedback, {
        interaction_uuid: target.interactionLogUuid,
        interaction_key: target.interactionKey,
        feedback_rating: "thumbs_down",
        support_follow_up_requested: true,
      });
      const interaction = res?.data?.redux?.payload?.interaction || {};
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedbackRating: interaction.feedback_rating || "thumbs_down",
                supportFollowUpStatus: interaction.support_follow_up_status || "requested",
                supportFollowUpSaving: false,
              }
            : message
        )
      );
    } catch (error) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                supportFollowUpStatus: previousStatus || null,
                supportFollowUpSaving: false,
              }
            : message
        )
      );
    }
  }, [messages]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue("");
    sendMessage(text);
  };

  // ── Push-to-talk voice input ──

  // Append a fresh red system bubble at the bottom of the message list. Used
  // by voice-input error paths to surface permission denial, recording
  // failure, and transcription failure with a consistent shape. (The chat
  // SSE error path uses a different pattern — it replaces the in-flight AI
  // bubble via aiMessageId, see onError around line 685.)
  const pushSystemMessage = (content) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `system-${Date.now()}`,
        content,
        sender: "system",
        timestamp: new Date(),
      },
    ]);
  };

  const releaseMicStream = () => {
    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch (_) {}
    }
  };

  const startRecording = async () => {
    if (voiceState !== "idle" || isStreaming) return;
    // Fresh token identifies this start attempt; teardownRecording nulls it.
    // If the token doesn't survive the getUserMedia await, the panel closed,
    // store changed, conversation reset, or component unmounted while the
    // browser's permission prompt was pending. We stop the freshly-acquired
    // stream's tracks ourselves so the mic doesn't stay live in a hidden /
    // reset UI.
    const startToken = {};
    pendingStartTokenRef.current = startToken;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (pendingStartTokenRef.current !== startToken) {
        // Teardown ran during the permission prompt — release the tracks
        // ourselves; mediaStreamRef.current is still null (teardown ran
        // before we assigned), so releaseMicStream() wouldn't see them.
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch (_) {}
        return;
      }
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];

      // Pick the first MediaRecorder mimeType the browser supports. Whisper
      // accepts webm and ogg/opus; Safari does neither, so we fall through
      // to mp4 (which Whisper also accepts). If none match, the browser
      // picks its own default and we send that.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mimeType =
        candidates.find((m) =>
          typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported
            ? MediaRecorder.isTypeSupported(m)
            : false
        ) || "";

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      mediaRecorderRef.current = recorder;
      recordingStartedAtRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start();
      setVoiceState("recording");
      pendingStartTokenRef.current = null;
    } catch (err) {
      // Suppress when teardown happened during the await — the rejection
      // (or denial) is a side-effect of that, not a real failure.
      if (pendingStartTokenRef.current !== startToken) return;
      pendingStartTokenRef.current = null;
      releaseMicStream();
      mediaRecorderRef.current = null;
      setVoiceState("idle");
      pushSystemMessage(
        err?.name === "NotAllowedError"
          ? "Mic permission denied. Enable microphone access in your browser to use voice input."
          : "Could not start recording. Try again."
      );
    }
  };

  // Hard teardown — stops the MediaRecorder without firing its upload path,
  // releases the mic stream, and aborts any in-flight transcription POST.
  // Reused by cancelRecording (user-initiated), the panel-close /
  // store-change effects, and the unmount cleanup so all four entry points
  // dispose state the same way.
  const teardownRecording = () => {
    // Invalidate any in-flight startRecording / stopRecordingAndTranscribe
    // so their post-await guards bail out instead of installing state onto
    // a torn-down panel.
    pendingStartTokenRef.current = null;
    pendingStopTokenRef.current = null;
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      } catch (_) {}
    }
    releaseMicStream();
    // CancelToken (axios 0.21.4) instead of AbortController — see the
    // comment in stopRecordingAndTranscribe for the version constraint.
    const source = transcribeAbortRef.current;
    transcribeAbortRef.current = null;
    if (source) {
      try {
        source.cancel("voice teardown");
      } catch (_) {}
    }
    pendingFocusRef.current = false;
  };

  const cancelRecording = () => {
    teardownRecording();
    setVoiceState("idle");
  };

  const stopRecordingAndTranscribe = () => {
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    if (!recorder) {
      setVoiceState("idle");
      return;
    }

    // Token identifies this stop attempt; teardownRecording nulls it. Set
    // BEFORE recorder.stop() so a teardown that fires in the gap between
    // stop() and onstop is observable inside the async onstop handler.
    const stopToken = {};
    pendingStopTokenRef.current = stopToken;

    const elapsedMs = Date.now() - (recordingStartedAtRef.current || 0);
    const recordedMimeType = recorder.mimeType || "audio/webm";

    recorder.onstop = async () => {
      releaseMicStream();
      const chunks = audioChunksRef.current;
      audioChunksRef.current = [];

      // Teardown fired between recorder.stop() and this handler running —
      // bail before installing a controller / firing the upload. The blob
      // is closure-local and gets garbage-collected when we return.
      if (pendingStopTokenRef.current !== stopToken) return;

      const blob = new Blob(chunks, { type: recordedMimeType });
      // Silence guard: ignore taps that produced no real audio.
      if (elapsedMs < 500 || blob.size < 1024) {
        pendingStopTokenRef.current = null;
        setVoiceState("idle");
        return;
      }

      setVoiceState("transcribing");

      // The repo is pinned to axios 0.21.4, which predates AbortController
      // support — the `signal` option is silently ignored. Use the legacy
      // CancelToken API so teardownRecording can actually cancel the
      // upload (otherwise the backend call keeps running and bills for
      // transcription even after the panel is hidden).
      const source = axios.CancelToken.source();
      transcribeAbortRef.current = source;

      try {
        const ext = recordedMimeType.includes("ogg")
          ? "ogg"
          : recordedMimeType.includes("mp4")
            ? "m4a"
            : "webm";
        const formData = new FormData();
        formData.append("store_id", String(sessionStoreId || homeStore));
        formData.append("audio", blob, `audio.${ext}`);

        const response = await axios.post(
          API_ops_assistant_transcribe_voice,
          formData,
          {
            ...getDefaultAxiosConfig(),
            // Sized to match the backend's transcription timeout. Without
            // this axios defaults to no timeout — a hung upload would leave
            // the composer stuck in "transcribing" indefinitely.
            timeout: 60000,
            cancelToken: source.token,
            // Let axios set the multipart boundary for us.
          }
        );
        const transcript = response?.data?.redux?.payload?.transcript || "";
        // Identity-check before landing the transcript. If teardown ran
        // during the fetch (panel close, store change, reset, unmount), the
        // response may have already resolved before the cancel fired —
        // setInputValue would otherwise drop a stale transcript into a
        // closed/reset textarea, possibly bound to the wrong store.
        if (transcript && transcribeAbortRef.current === source) {
          setInputValue((prev) =>
            prev ? `${prev.replace(/\s+$/, "")} ${transcript}` : transcript
          );
          // Defer focus to the effect below — the textarea is still disabled
          // until voiceState flips to "idle" in the finally block, so focusing
          // here is a no-op.
          pendingFocusRef.current = true;
        }
      } catch (err) {
        // Suppress errors from a teardown-initiated cancel — the user
        // closed the panel or switched stores; we shouldn't surface that
        // as a transcription failure.
        if (axios.isCancel(err)) return;
        pushSystemMessage(
          err?.response?.data?.system?.message ||
            (err?.code === "ECONNABORTED"
              ? "Transcription timed out. Try a shorter recording."
              : "Could not transcribe audio. Try again.")
        );
      } finally {
        if (transcribeAbortRef.current === source) {
          transcribeAbortRef.current = null;
        }
        if (pendingStopTokenRef.current === stopToken) {
          pendingStopTokenRef.current = null;
        }
        setVoiceState("idle");
      }
    };

    try {
      recorder.stop();
    } catch (_) {
      releaseMicStream();
      setVoiceState("idle");
    }
  };

  // Tear down any in-flight recording when the panel closes — the component
  // returns null but stays mounted (line 957), so the unmount cleanup below
  // won't fire and a forgotten recording would keep the mic open and the
  // browser's recording indicator lit. teardownRecording is idempotent on
  // null refs, so firing it on every close is safe. Critically: we DO NOT
  // gate this on voiceState !== "idle" — startRecording can be awaiting
  // getUserMedia with voiceState still "idle" and pendingStartTokenRef set,
  // and teardownRecording is what nulls that token so the late-resolving
  // startup bails instead of installing a stream onto a closed panel.
  useEffect(() => {
    if (panelState === "closed") {
      teardownRecording();
      setVoiceState("idle");
    }
  }, [panelState]);

  // (Voice teardown for store-change is handled inline in the homeStore-keyed
  // reset effect above, alongside the chat stream abort — keeping all reset
  // bookkeeping in one place so they stay in sync.)

  // Final safety net: stop any in-flight recording and chat stream on unmount.
  useEffect(() => {
    return () => {
      teardownRecording();
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  // Focus the textarea once a successful transcription has dropped its
  // transcript and voiceState has flipped back to idle (so the textarea is
  // enabled again). Without this the keyboard never appears after dictation.
  useEffect(() => {
    if (voiceState === "idle" && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      textareaRef.current?.focus();
    }
  }, [voiceState]);

  // Update proposal status in messages state so it persists across re-renders
  const handleProposalStatusChange = (messageId, proposalIds, newStatus) => {
    const ids = Array.isArray(proposalIds) ? proposalIds : [proposalIds];
    if (isSetupMode && (newStatus === "cancelled" || newStatus === "rejected") && batchConfirmedTitleRef.current) {
      // The batch had a confirmed card; if this cancellation resolved its
      // last pending card, Lumi still takes the next step.
      scheduleNextStepTurn(batchConfirmedTitleRef.current, ids);
    }
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId || !msg.proposals) return msg;
        const ids = Array.isArray(proposalIds) ? proposalIds : [proposalIds];
        return {
          ...msg,
          proposals: msg.proposals.map((p) =>
            ids.includes(p.proposal_id) ? { ...p, status: newStatus } : p
          ),
        };
      })
    );
  };

  // Setup mode: after a credential card connects, Lumi takes the next turn.
  // The send is deferred to an effect so it runs against messages state that
  // already holds the card's "connected" status (sendMessage snapshots
  // `messages`; calling it in the same tick would clobber that update).
  // If a stream is already in flight, hold the follow-up until it finishes
  // (sendMessage drops sends while streaming, which would lose the turn).
  useEffect(() => {
    if (!pendingSetupFollowUp || isStreaming) return;
    setPendingSetupFollowUp(null);
    // sendMessage sets isStreaming synchronously, which hides the chips
    // from here on.
    setNextStepPending(false);
    if (typeof pendingSetupFollowUp === "string") {
      sendMessage(pendingSetupFollowUp);
    } else {
      sendMessage(pendingSetupFollowUp.text, { hidden: Boolean(pendingSetupFollowUp.hidden) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSetupFollowUp, isStreaming]);

  // Setup mode: the parsed roster from the upload card. Sent as
  // roster_upload with every setup turn while present and cleared when the
  // import card resolves, the card is dismissed, or the conversation resets.

  // Setup mode: persist a roster card's status (and upload outcome) in
  // messages state. Only counts are stored on the message; rows live in
  // setupRoster.
  const handleRosterUploadStatus = (messageId, requestId, newStatus, outcome) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId || !msg.rosterUploadRequest) return msg;
        if (msg.rosterUploadRequest.request_id !== requestId) return msg;
        return {
          ...msg,
          rosterUploadRequest: {
            ...msg.rosterUploadRequest,
            status: newStatus,
            ...(outcome
              ? { uploaded_count: outcome.count, file_name: outcome.file_name }
              : {}),
          },
        };
      })
    );
  };

  // Setup mode: persist a credential card's status in messages state.
  const handleCredentialRequestStatus = (messageId, requestId, newStatus) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId || !msg.credentialRequest) return msg;
        if (msg.credentialRequest.request_id !== requestId) return msg;
        return { ...msg, credentialRequest: { ...msg.credentialRequest, status: newStatus } };
      })
    );
  };

  const resetConversation = () => {
    // Tear down any active recording / in-flight transcription before we
    // clear the conversation. Without this, the header "Reset" button leaves
    // the mic running or an upload in flight under a freshly-reset UI — a
    // later stop/response would then append the stale transcript into the
    // new conversation. teardownRecording also aborts the transcribe
    // controller and resets pendingFocusRef.
    teardownRecording();
    setVoiceState("idle");
    lastFollowTargetRef.current = null;
    checklistShownForRef.current = setupSummaryRef.current;
    setEngagedStepKey(null);
    // A fresh conversation may start with a skip chip (no visible message),
    // so the once-per-batch guard must not carry over.
    nextStepSentRef.current = false;
    welcomesDeclinedRef.current = null;
    engagedOverrideRef.current = null;
    setMessages([buildGreetingMessage()]);
    setSessionUuid(null);
    setSessionStoreId(homeStore);
    setExpandedSources({});
    setSetupRoster(null);
    setPendingSetupFollowUp(null);
    setNextStepPending(false);
    if (nextStepTimerRef.current) { clearTimeout(nextStepTimerRef.current); nextStepTimerRef.current = null; }
    // Invalidate any upload still in flight from the discarded conversation.
    conversationGenRef.current += 1;
  };

  // Confirmed proposals: notify the parent (refresh categories) and, in setup
  // mode, refetch the checklist so the greeting/progress stay current.
  // The summary refetch waits for the parent's refresh (when it returns a
  // promise) so the next greeting reflects stores created through chat.
  // After a confirmed setup card, Lumi takes the next turn itself (announce
  // what happened, start the next step). A burst of cards confirmed together
  // (several locations, several roles) yields one turn.
  // One next-step turn per batch of cards: only once every card in the
  // transcript is resolved, and never twice without a real user message in
  // between (the flag resets in sendMessage for visible messages).
  const scheduleNextStepTurn = (label, resolvedProposalId) => {
    const generationAtStart = conversationGenRef.current;
    // One id, or every id a bulk card just resolved (state may not have
    // committed yet when this runs).
    const resolved = new Set([].concat(resolvedProposalId || []));
    // The location the resolved card acted on: company-scoped review steps
    // (labor laws) carry no store of their own, so the page opens on this one.
    // Company-scoped setup cards (set_location_address) pin their store in
    // the private _store_id arg rather than on the proposal itself.
    const proposalStore = (p) => p?.store_id || p?.tool_args?._store_id || null;
    const resolvedStoreId = proposalStore(
      messagesRef.current
        .flatMap((m) => m.proposals || [])
        .find((p) => resolved.has(p.proposal_id) && proposalStore(p))
    );
    const stillPending = messagesRef.current.some((m) =>
      (m.proposals || []).some(
        (p) => p.status === "pending" && !resolved.has(p.proposal_id)
      )
    );
    if (stillPending || nextStepSentRef.current) {
      // No next-step turn is coming from this resolution, so the chips may
      // show again (a still-pending card hides them on its own).
      setNextStepPending(false);
      return;
    }
    nextStepSentRef.current = true;
    batchConfirmedTitleRef.current = null;
    setNextStepPending(true);
    // A simplified checklist with the finished step checked, before Lumi's
    // own next-step message.
    const items = buildChecklistItems(setupSummaryRef.current);
    if (items.length > 0) {
      const highlightKeys = completedBetween(checklistShownForRef.current, setupSummaryRef.current);
      checklistShownForRef.current = setupSummaryRef.current;
      setMessages((prev) => [
        ...prev,
        { id: `checklist-${Date.now()}`, sender: "checklist", items, highlightKeys, timestamp: new Date() },
      ]);
    }
    if (nextStepTimerRef.current) clearTimeout(nextStepTimerRef.current);
    nextStepTimerRef.current = setTimeout(async () => {
      nextStepTimerRef.current = null;
      // Ground the turn in the refreshed checklist so Lumi continues with
      // the real next step instead of inferring one.
      const nba = setupSummaryRef.current?.nextBestAction;
      let nextKey = nba?.key || null;
      let nextTitle = nba?.title;
      let nextStoreId = nba?.store_id;
      let engagedKey = currentStepChipKey(setupSummaryRef.current);
      let deferred = "";
      // The invite step needs nothing from Lumi right now: either welcomes
      // were turned off on the import card just confirmed (deferred by
      // choice) or they already went out with it (in progress until people
      // accept). Either way she moves past it rather than offering to send.
      const welcomesSent = welcomeStepStatus(setupSummaryRef.current) === "in_progress";
      // The deferral belongs to the location whose roster declined the
      // welcomes (recorded at confirm time), never to whichever card of the
      // batch happened to resolve last or whichever location the checklist
      // put first.
      const declined = welcomesDeclinedRef.current;
      welcomesDeclinedRef.current = null;
      const declinedStoreId = declined?.storeId ?? null;
      const deferredStoreId = declinedStoreId || resolvedStoreId || nba?.store_id || null;
      const nextIsDeferredStore =
        nextKey === "welcome_message_sent" &&
        (!deferredStoreId || !nba?.store_id || Number(nba.store_id) === Number(deferredStoreId));
      if (declined && declinedStoreId) {
        // Record the declined store's own skip server-side, and wait for it
        // (and the summary refresh it triggers) so the follow-up turn is
        // built against the deferred state and a failed skip is not papered
        // over by the conversation moving on.
        const userTurnAtSkip = userTurnSeqRef.current;
        const skip = deferSkipActionFor(setupSummaryRef.current, "welcome_message_sent", declinedStoreId);
        if (skip.action) {
          const ok = await handleOnboardingAction(skip.action, setupActionOptions);
          if (generationAtStart !== conversationGenRef.current) return;
          if (userTurnSeqRef.current !== userTurnAtSkip) {
            // The user took the turn while the skip was in flight: the skip
            // itself stands, but no follow-up may run behind their reply.
            setNextStepPending(false);
            return;
          }
          if (ok === false) {
            // The skip did not persist: the declined location is still
            // unskipped, so no follow-up may claim the flow moved past it.
            setNextStepPending(false);
            return;
          }
        }
      }
      if (nextIsDeferredStore && (declined || welcomesSent)) {
        const after = nextActionableStepAfter(setupSummaryRef.current, "welcome_message_sent", deferredStoreId);
        deferred = declined
          ? " Welcome messages were turned off on that card on purpose: do not ask about invites and do not propose sending any; say in one clause that invites can go out any time, then continue with the next step."
          : " The welcome invites already went out with that card: say so in one clause (the step completes as people accept), never say they have not been sent, do not ask about sending or resending them, then continue with the next step.";
        if (after) {
          nextKey = after.key;
          nextTitle = after.title;
          nextStoreId = after.store_id;
          engagedKey = after.key;
          engagedOverrideRef.current = after.key;
        }
      }
      const nextStep = nextTitle ? ` The checklist's next step is: ${nextTitle}.` : "";
      // Move the screen to the next step before Lumi starts it.
      const nextTarget = nextKey ? followAlongTargetForTask(nextKey, nextStoreId || resolvedStoreId) : null;
      // Lumi starts the step the chip row would offer, so mark that one.
      setEngagedStepKey(engagedKey);
      const opened = nextTarget && followAlongNavRef.current(nextTarget) ? ` The app has opened ${nextTarget.label} behind the chat.` : "";
      setPendingSetupFollowUp({
        text: `[Card confirmed: ${label}] That change is already applied.${nextStep}${deferred}${opened} Say what happened in one sentence, then start that step now: call its tool only if I have already given every value it needs; otherwise ask me for the one value that is missing and never guess it. Do not propose the same card again and do not navigate.`,
        hidden: true,
      });
    }, 400);
  };
  useEffect(() => () => {
    if (nextStepTimerRef.current) clearTimeout(nextStepTimerRef.current);
  }, []);

  const notifyActionConfirmed = async (toolNames, cardTitle, proposalId) => {
    // A company switch during either await invalidates this confirmation:
    // its summary and next-step turn belong to the previous conversation.
    const generation = conversationGenRef.current;
    const userTurn = userTurnSeqRef.current;
    // The user sent a message while the refreshes below were running: they
    // have the turn, so this confirmation queues no next-step turn.
    const userTookOver = () => userTurnSeqRef.current !== userTurn;
    // Hide the chips now: the next-step turn is scheduled only after the
    // refreshes below, and the chip row must not flash in between. A company
    // switch mid-await clears the flag in the scope effect.
    if (isSetupMode && cardTitle) setNextStepPending(true);
    try {
      await onActionConfirmed?.(toolNames);
    } catch (error) {
      console.error("Failed to refresh after a confirmed assistant action", error);
    }
    if (generation !== conversationGenRef.current) return;
    if (isSetupMode) {
      if (userTookOver()) {
        setNextStepPending(false);
        return;
      }
      if (cardTitle) batchConfirmedTitleRef.current = cardTitle;
      // Refresh first so the next-step turn sees the updated checklist.
      await refreshSetupSummary();
      if (generation !== conversationGenRef.current) return;
      if (userTookOver()) {
        batchConfirmedTitleRef.current = null;
        setNextStepPending(false);
        return;
      }
      if (cardTitle) scheduleNextStepTurn(cardTitle, proposalId);
    }
  };

  // Setup mode: proposal cards confirm against the company-scoped endpoint.
  const setupCardProps = isSetupMode
    ? {
        confirmUrl: API_setup_assistant_confirm_action,
        bulkConfirmUrl: API_setup_assistant_bulk_confirm,
        scopeParams: { company_id: homeCompany },
      }
    : {};
  const setupActionOptions = {
    companyId: homeCompany,
    setCurrentTab,
    refreshSummary: refreshSetupSummary,
    // Section-only actions have no Onboarding Center scroll target inside the
    // panel, so hand the section to the Onboarding Center via intent.
    setRequestedSection: (section) => {
      setOnboardingIntent({ tab: "onboardingCenter", section });
      setCurrentTab("onboardingCenter");
    },
    openModal: null,
    homeStore,
    changeHomeStore: change_home_store,
  };
  // Follow along: open the target screen behind the chat. Returns true when
  // it navigated. Skipped when off, on narrow viewports (the docked panel
  // would cover the page), or when the screen is already showing the target.
  const navigateFollowAlong = (target) => {
    if (!isSetupMode || !target) return false;
    if (!followAlongViewportAllows()) return false;
    if (isSameFollowAlongTarget(lastFollowTargetRef.current, target)) return false;
    lastFollowTargetRef.current = target;
    const { label, ...action } = target;
    Promise.resolve(handleOnboardingAction(action, setupActionOptions)).catch((error) => {
      console.error("Follow-along navigation failed", error);
    });
    return true;
  };
  followAlongNavRef.current = navigateFollowAlong;
  // Setup mode: a taskKey action advances the checklist on the server, so the
  // parent must refresh the same surfaces a confirmed mark_setup_step would.
  const runSetupAction = async (setupAction) => {
    let action = setupAction?.action || setupAction;
    // A navigation pill for a company-scoped step (labor laws) names no
    // location; open the page on the step's store, else the only real one,
    // so the page can focus that location's card.
    if (action?.tab && !action.store_id) {
      const storeId = setupAction?.store_id || onlyRealStoreId(setupSummaryRef.current);
      if (storeId) action = { ...action, store_id: storeId };
    }
    const ok = await handleOnboardingAction(action, setupActionOptions);
    if (action?.taskKey && ok !== false) {
      notifyActionConfirmed(["mark_setup_step"]);
    }
  };

  // Walk back from the given message index to find the most recent chart's
  // current widget_type. Reflects any in-chat dropdown edits the user made.
  // Save proposals usually arrive on a new message after "save this", so the
  // relevant chart lives on an earlier message.
  const getChartWidgetTypeForMessage = (index) => {
    for (let i = index; i >= 0; i--) {
      const wt = messages[i]?.chartData?.widget_type;
      if (wt) return wt;
    }
    return null;
  };

  const getChartLayoutForMessage = (index) => {
    for (let i = index; i >= 0; i--) {
      if (messages[i]?.chartData) return messages[i].chartData.layout || "over_time";
    }
    return null;
  };

  const formatTimestamp = (ts) =>
    new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(ts);

  const markdownComponents = {
    p: ({ node, ...props }) => <p className="mt-2 text-sm" {...props} />,
    strong: ({ node, ...props }) => <strong className="font-semibold text-sm" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc ml-5 mt-2 text-sm" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mt-2 text-sm" {...props} />,
    li: ({ node, ...props }) => <li className="mt-1 text-sm" {...props} />,
  };

  if (panelState === "closed") return null;
  if (setupComplete && farewellDismissed) return null;

  const isExpanded = panelState === "expanded";

  // Anchor panel's bottom-right corner to the FAB position, clamped to viewport
  const panelWidth = panelSize.w;
  const panelHeight = panelSize.h;
  const clampedRight = Math.max(0, Math.min(fabPosition.right, window.innerWidth - panelWidth));
  const clampedBottom = Math.max(0, Math.min(fabPosition.bottom, window.innerHeight - panelHeight));

  let panelStyle;
  if (isExpanded) {
    panelStyle = {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1400,
    };
  } else if (docked) {
    panelStyle = {
      position: "fixed",
      top: 0,
      bottom: 0,
      left: 60,
      width: panelWidth,
      zIndex: 1100,
      boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
    };
  } else {
    panelStyle = {
      position: "fixed",
      bottom: clampedBottom,
      right: clampedRight,
      width: panelWidth,
      height: panelHeight,
      zIndex: 1400,
      borderRadius: 12,
      transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    };
  }

  // Setup is done: a closing card in place of the conversation.
  if (setupComplete) {
    const farewell = buildSetupFarewell(setupSummary, {
      rovisAvailable: Boolean(onOpenOperationsAssistant),
    });
    return (
      <div
        style={{ ...panelStyle, height: "auto" }}
        className="flex flex-col bg-white overflow-hidden relative"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 bg-violet-900 text-white">
          <img
            src="/images/mascot-floating.png"
            alt=""
            aria-hidden="true"
            className="w-8 h-8 object-contain"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-medium">{SETUP_ASSISTANT_NAME}</span>
            <span className="text-[11px] text-violet-100">Setup complete</span>
          </div>
        </div>
        <div className="p-4 text-sm text-gray-800">{farewell}</div>
        <div className="flex justify-end gap-2 px-4 pb-4">
          <button
            type="button"
            onClick={() => setFarewellDismissed(true)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          {onOpenOperationsAssistant && (
            <button
              type="button"
              onClick={() => {
                setFarewellDismissed(true);
                onOpenOperationsAssistant();
              }}
              className="rounded-lg bg-violet-700 px-3 py-1.5 text-sm text-white hover:bg-violet-800"
            >
              Meet Rovis
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRootRef}
      style={panelStyle}
      className="flex flex-col bg-white overflow-hidden relative"
      onMouseDown={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
    >
      {/* Resize edges and corners */}
      {!isExpanded && !docked && (
        <>
          {/* Edges */}
          <div className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize z-10" onMouseDown={handleResizeMouseDown("top")} />
          <div className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize z-10" onMouseDown={handleResizeMouseDown("bottom")} />
          <div className="absolute top-0 left-0 bottom-0 w-1.5 cursor-ew-resize z-10" onMouseDown={handleResizeMouseDown("left")} />
          <div className="absolute top-0 right-0 bottom-0 w-1.5 cursor-ew-resize z-10" onMouseDown={handleResizeMouseDown("right")} />
          {/* Corners */}
          <div className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-20" onMouseDown={handleResizeMouseDown("top-left")} />
          <div className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-20" onMouseDown={handleResizeMouseDown("top-right")} />
          <div className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-20" onMouseDown={handleResizeMouseDown("bottom-left")} />
          <div className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-20" onMouseDown={handleResizeMouseDown("bottom-right")} />
        </>
      )}
      {docked && (
        <div className="absolute top-0 right-0 bottom-0 w-1.5 cursor-ew-resize z-10" onMouseDown={handleResizeMouseDown("right")} />
      )}

      {/* Header / Drag handle */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-violet-700 text-white select-none shrink-0"
        style={{ cursor: isExpanded ? "default" : "grab" }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-visible">
            <span
              aria-hidden="true"
              className="absolute bottom-[-3px] left-1/2 h-3 w-8 -translate-x-1/2 rounded-full bg-violet-950/40 blur-md"
            />
            <img
              src="/images/mascot-floating.png"
              alt=""
              aria-hidden="true"
              className="relative z-10 -translate-y-0.5 w-10 h-10 object-contain [filter:drop-shadow(0_10px_14px_rgba(46,16,101,0.18))]"
            />
          </span>
          <div className="flex flex-col leading-tight">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {isSetupMode ? SETUP_ASSISTANT_NAME : "Rovis"}
              </span>
              <span className="rounded-full border border-violet-200/60 bg-violet-100/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-50">
                Beta
              </span>
            </div>
            <span className="text-[11px] text-violet-100">
              {isSetupMode ? "Setup Assistant" : "Operations Assistant"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>
          <button
            onClick={resetConversation}
            className="text-xs text-violet-200 hover:text-white px-1"
          >
            Reset
          </button>
          <button
            onClick={() => {
              if (docked) {
                setDocked(false);
                setDragOffset({ x: 0, y: 0 });
              } else {
                setDocked(true);
                setPanelSize((prev) => ({ ...prev, w: Math.min(prev.w, 360) }));
                setDragOffset({ x: 0, y: 0 });
              }
            }}
            className="w-6 h-6 flex items-center justify-center hover:bg-violet-800 rounded"
            title={docked ? "Undock" : "Dock to side"}
          >
            <DockIcon style={{ fontSize: 16 }} />
          </button>
          <button
            onClick={() => setPanelState(isExpanded ? "default" : "expanded")}
            className="w-6 h-6 flex items-center justify-center hover:bg-violet-800 rounded"
          >
            {isExpanded ? (
              <FullscreenExitIcon style={{ fontSize: 16 }} />
            ) : (
              <FullscreenIcon style={{ fontSize: 16 }} />
            )}
          </button>
          <button
            onClick={() => setPanelState("closed")}
            className="w-6 h-6 flex items-center justify-center hover:bg-violet-800 rounded"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-3 bg-gray-50"
        style={cardsAreCancelling ? { pointerEvents: "none" } : undefined}
      >
       <div ref={messagesContentRef}>
        {messages.map((message, messageIndex) => message.hidden ? null : message.sender === "checklist" ? (
          <div key={message.id} className="mb-3 flex justify-start">
            <div className="max-w-[85%] w-full">
              <SetupChecklistCard items={message.items} highlightKeys={message.highlightKeys} />
            </div>
          </div>
        ) : (
          <div
            key={message.id}
            className={`mb-3 flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`rounded-lg px-3 py-2 ${
                message.chartData ? "w-[95%] max-w-[95%]" : "max-w-[85%]"
              } ${
                message.sender === "user"
                  ? "bg-violet-100 text-gray-700"
                  : message.sender === "system"
                  ? "bg-red-50 border border-red-200 text-red-700"
                  : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              {message.sender === "ai" && message.loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500" role="status" aria-label="Loading your setup">
                  <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                  <span>Loading your setup...</span>
                </div>
              ) : message.sender === "ai" ? (
                <ReactMarkdown components={markdownComponents}>
                  {message.content}
                </ReactMarkdown>
              ) : (
                <div className="text-sm" title={message.displayLabel ? message.content : undefined}>
                  {message.displayLabel || message.content}
                </div>
              )}

              {/* Knowledge-base source footer — same pattern AssistantDisplay.js
                  uses for the documents chat, kept identical so future tweaks
                  to citation UX land in one place. */}
              {message.sender === "ai" && message.sources && message.sources.length > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <button
                    onClick={() => toggleSources(message.id)}
                    className="flex items-center text-xs text-violet-600 hover:text-violet-800 transition-colors mb-1"
                  >
                    <ArticleIcon fontSize="small" className="mr-1" style={{ fontSize: "14px" }} />
                    <span>
                      {message.sources.length} source{message.sources.length > 1 ? "s" : ""}
                    </span>
                    {expandedSources[message.id] ? (
                      <ExpandLessIcon fontSize="small" className="ml-1" style={{ fontSize: "14px" }} />
                    ) : (
                      <ExpandMoreIcon fontSize="small" className="ml-1" style={{ fontSize: "14px" }} />
                    )}
                  </button>

                  {expandedSources[message.id] && (
                    <div className="mt-2 bg-gray-50 rounded p-3 text-xs">
                      {message.sources.map((source, index) => (
                        <div key={source.id} className={`${index > 0 ? "mt-2 pt-3 pb-2 border-t border-gray-100" : ""}`}>
                          <a
                            href={getSafeSourceHref(source.path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex space-x-2 hover:bg-gray-100 p-1 rounded transition-colors"
                          >
                            <div className="rounded w-5 h-6 flex items-center justify-center border border-gray-200 bg-white">
                              {source.type === "LearningSection" ? (
                                <LocalLibraryIcon style={{ fontSize: "14px" }} className="text-gray-500" />
                              ) : (
                                <SubjectIcon style={{ fontSize: "16px" }} className="text-gray-500" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-700">{source.title}</div>
                              {source.fileSize && (
                                <div className="text-gray-500 truncate mt-1.5 flex items-center">
                                  <span className="text-gray-400">{formatFileSize(source.fileSize)}</span>
                                </div>
                              )}
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Setup mode: Onboarding Center actions as violet pills. */}
              {isSetupMode && message.sender === "ai" && message.setupActions?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {message.setupActions.map((setupAction, index) => (
                    <button
                      key={`${setupAction.task_key || "action"}-${setupAction.store_id || "company"}-${index}`}
                      type="button"
                      onClick={() => runSetupAction(setupAction)}
                      className="rounded-full bg-violet-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-violet-800"
                    >
                      {setupAction.label || setupAction.action?.label || "Open"}
                    </button>
                  ))}
                </div>
              )}

              {/* Setup mode: secure POS credential card. Values stay inside
                  the card; only the outcome comes back here. */}
              {isSetupMode && message.sender === "ai" && message.credentialRequest && (
                <PosCredentialCard
                  request={message.credentialRequest}
                  companyId={homeCompany}
                  status={message.credentialRequest.status}
                  onStatusChange={(requestId, newStatus) =>
                    handleCredentialRequestStatus(message.id, requestId, newStatus)
                  }
                  onConnected={() => {
                    notifyActionConfirmed(["connect_pos"]);
                    // Backend title is imperative ("Connect Toast"); the
                    // follow-up turn needs just the provider name.
                    const label =
                      (message.credentialRequest.title || "").replace(/^Connect\s+/i, "") ||
                      "Your POS";
                    setPendingSetupFollowUp(`${label} is connected.`);
                  }}
                />
              )}

              {/* Setup mode: roster upload card. The file goes straight to
                  the roster endpoint; only parsed rows come back here. */}
              {isSetupMode && message.sender === "ai" && message.rosterUploadRequest && (
                <RosterUploadCard
                  request={message.rosterUploadRequest}
                  companyId={homeCompany}
                  status={message.rosterUploadRequest.status}
                  onStatusChange={(requestId, newStatus, outcome) =>
                    handleRosterUploadStatus(message.id, requestId, newStatus, outcome)
                  }
                  onUploaded={(parsed) => {
                    // An upload that finishes after the user switched
                    // companies belongs to the old conversation: drop it.
                    if (conversationGenAtRender !== conversationGenRef.current) return;
                    setSetupRoster({
                      file_name: parsed.file_name,
                      store_id: message.rosterUploadRequest.store_id,
                      store_name: message.rosterUploadRequest.store_name,
                      rows: parsed.rows,
                    });
                    const count = parsed.counts?.total ?? parsed.rows.length;
                    const target = message.rosterUploadRequest.store_name
                      ? ` to ${message.rosterUploadRequest.store_name}`
                      : "";
                    setPendingSetupFollowUp(
                      `Roster uploaded: ${count} people found. Add them${target}.`
                    );
                  }}
                />
              )}

              {/* Product-help "Show me" navigation buttons — switch the
                  main tab and pulse-highlight the data-help-id anchor.
                  Payloads come from get_product_help via done.help_navigations. */}
              {message.sender === "ai" && message.helpNavs && message.helpNavs.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {message.helpNavs.map((nav) => (
                    <button
                      key={nav.topic_id}
                      onClick={() => navigateAndHighlight(nav, setCurrentTab)}
                      className="flex items-center gap-x-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-medium rounded-lg transition-colors"
                    >
                      <NearMeIcon style={{ fontSize: "14px" }} />
                      <span>{nav.label || "Show me"}</span>
                    </button>
                  ))}
                </div>
              )}

              {message.sender === "ai" &&
                (message.interactionLogUuid || message.interactionKey) &&
                !(Array.isArray(message.proposals) && message.proposals.length > 0) && (
                <div className="mt-3 border-t border-gray-100 pt-2">
                  <div className="flex items-center gap-0.5">
                    <Tooltip title={copiedMessageId === message.id ? "Copied" : "Copy"} arrow>
                      <button
                        type="button"
                        aria-label="Copy response"
                        onClick={() => handleCopyAssistantMessage(message.id, message.content)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <ContentCopyIcon style={{ fontSize: "17px" }} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Good response" arrow>
                      <span>
                        <button
                          type="button"
                          aria-label="Good response"
                          disabled={message.feedbackSaving}
                          onClick={() => handleAssistantFeedback(message.id, "thumbs_up")}
                          className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                            message.feedbackRating === "thumbs_up"
                              ? "bg-gray-100 text-gray-800"
                              : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                          } ${message.feedbackSaving ? "opacity-60" : ""}`}
                        >
                          {message.feedbackRating === "thumbs_up" ? (
                            <ThumbUpIcon style={{ fontSize: "17px" }} />
                          ) : (
                            <ThumbUpOffAltIcon style={{ fontSize: "17px" }} />
                          )}
                        </button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bad response" arrow>
                      <span>
                        <button
                          type="button"
                          aria-label="Bad response"
                          disabled={message.feedbackSaving}
                          onClick={() => handleAssistantFeedback(message.id, "thumbs_down")}
                          className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                            message.feedbackRating === "thumbs_down"
                              ? "bg-gray-100 text-gray-800"
                              : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                          } ${message.feedbackSaving ? "opacity-60" : ""}`}
                        >
                          {message.feedbackRating === "thumbs_down" ? (
                            <ThumbDownIcon style={{ fontSize: "17px" }} />
                          ) : (
                            <ThumbDownOffAltIcon style={{ fontSize: "17px" }} />
                          )}
                        </button>
                      </span>
                    </Tooltip>
                  </div>
                  {message.feedbackRating === "thumbs_down" && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      {message.supportFollowUpSaving ? (
                        <span className="text-gray-500">Notifying Rightwork support...</span>
                      ) : message.supportFollowUpStatus === "sent" ? (
                        <span className="text-gray-500">Rightwork support notified</span>
                      ) : (
                        <>
                          <span>
                            {message.supportFollowUpStatus === "failed"
                              ? "Could not send."
                              : "Want Rightwork to review this?"}
                          </span>
                          <button
                            type="button"
                            disabled={message.supportFollowUpSaving}
                            onClick={() => handleAssistantSupportRequest(message.id)}
                            className="font-medium text-violet-700 hover:text-violet-800 disabled:opacity-60"
                          >
                            {message.supportFollowUpStatus === "failed" ? "Try again" : "Notify support"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Inline chart — sized to the ops panel (not shared with dashboards / AI reporting) */}
              {message.chartData && message.chartData.metric_results && (() => {
                const widgetType = message.chartData.widget_type;
                // Height scales with panel state so charts fit tiny panels and full screen alike.
                // Also capped by the chart width so narrow panels don't render tall, skinny charts.
                const widthRef = isExpanded ? window.innerWidth : panelSize.w;
                const maxByWidth = widthRef * 0.7;
                let chartHeight;
                if (isExpanded) {
                  chartHeight = Math.max(320, Math.min(window.innerHeight * 0.55, 560));
                } else if (docked) {
                  chartHeight = Math.max(220, Math.min(window.innerHeight * 0.45, 420));
                } else {
                  chartHeight = Math.max(180, Math.min(panelSize.h * 0.5, 360));
                }
                chartHeight = Math.min(chartHeight, maxByWidth);
                // Axis/legend/title font sizes scale with available chart height.
                const tickFontSize = chartHeight < 260 ? 9 : chartHeight < 380 ? 10 : 12;
                const legendFontSize = chartHeight < 260 ? 10 : chartHeight < 380 ? 11 : 13;
                const titleFontSize = chartHeight < 260 ? 11 : chartHeight < 380 ? 12 : 14;
                return (
                  <div className="ops-chart-scope mt-3 mb-1 bg-white border border-gray-200 rounded-lg p-3">
                    <style>{`
                      .ops-chart-scope .recharts-cartesian-axis-tick-value,
                      .ops-chart-scope .recharts-polar-angle-axis-tick text { font-size: ${tickFontSize}px; }
                      .ops-chart-scope .recharts-legend-item-text { font-size: ${legendFontSize}px !important; }
                      .ops-chart-scope .recharts-default-legend { font-size: ${legendFontSize}px !important; }
                    `}</style>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4
                        className="font-semibold text-gray-700 break-words"
                        style={{ fontSize: titleFontSize, lineHeight: 1.3 }}
                      >
                        {message.chartData.title}
                      </h4>
                      <Select
                        value={widgetType}
                        onChange={(e) => {
                          const newType = e.target.value;
                          setMessages((prev) =>
                            prev.map((msg) =>
                              msg.id === message.id
                                ? { ...msg, chartData: { ...msg.chartData, widget_type: newType } }
                                : msg
                            )
                          );
                        }}
                        input={<OutlinedInput />}
                        size="small"
                        MenuProps={{ sx: { zIndex: 1500 } }}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          height: "28px",
                          fontSize: "12px",
                          "& .MuiSelect-select": {
                            paddingTop: 0,
                            paddingBottom: 0,
                            display: "flex",
                            alignItems: "center",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                      >
                        {/* A by_entity chart only renders as bar or table. */}
                        {message.chartData.layout !== "by_entity" && <MenuItem value="line">Line</MenuItem>}
                        <MenuItem value="bar">Bar</MenuItem>
                        {message.chartData.layout !== "by_entity" && <MenuItem value="pie">Pie</MenuItem>}
                        <MenuItem value="table">Table</MenuItem>
                        {message.chartData.layout !== "by_entity" && <MenuItem value="cards">Cards</MenuItem>}
                      </Select>
                    </div>
                    <div style={{
                      // Charts own their height (and entity-bar scrolling). Let the
                      // widget's help row add to it so content stays above the timestamp.
                      minHeight: chartHeight,
                      height: ["table", "cards"].includes(widgetType) ? chartHeight : "auto",
                    }}>
                      <WidgetRenderer
                        widgetType={widgetType}
                        activeTab={message.chartData.metric_results.active_tab}
                        data={message.chartData.metric_results.agg_data}
                        rawData={message.chartData.metric_results.raw_data}
                        intradayData={message.chartData.metric_results.intraday_data}
                        summaryData={message.chartData.metric_results.summary_data}
                        locationData={message.chartData.metric_results.location_data}
                        selectedMetrics={message.chartData.metric_results.selected_metrics}
                        metrics={message.chartData.metric_results.metrics_info}
                        compareLocations={message.chartData.metric_results.compare_locations}
                        compareDow={message.chartData.metric_results.compare_dow}
                        layout={message.chartData.layout}
                        isDashboard={false}
                        chartHeight={chartHeight}
                        compactAxis
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Action proposals */}
              {message.proposals &&
                (() => {
                  const MESSAGING_TOOLS = ["send_channel_message", "send_direct_message", "create_group_chat"];
                  const LOGBOOK_TOOLS = ["create_logbook_entry"];
                  const DAY_NOTE_TOOLS = ["add_day_note"];
                  const TRAINING_INPUT_TOOLS = ["submit_manager_training_input"];
                  const TRAINING_CHECKPOINT_TOOLS = ["complete_training_block"];
                  const FORM_GRADE_TOOLS = ["grade_form"];
                  const SAVE_REPORT_TOOLS = ["save_report_to_dashboard"];
                  const INVITE_TOOLS = ["invite_employee"];
                  const ROSTER_TOOLS = ["import_roster"];
                  const ROLES_TOOLS = isSetupMode ? ["create_roles"] : [];
                  const LOCATION_CREATE_TOOLS = isSetupMode ? ["create_location"] : [];
                  const LOCATION_SETTINGS_TOOLS = isSetupMode ? ["update_store_settings", "set_location_address"] : [];
                  const TIME_OFF_TOOLS = ["create_time_off"];
                  const AVAILABILITY_TOOLS = ["set_employee_availability"];
                  // Publish has a much wider blast radius than other scheduling
                  // mutations and re-runs labor-law checks against the freshly
                  // mutated draft. Always render it as its own ActionCard so the
                  // manager confirms it deliberately and any per-action error
                  // (e.g. blocking labor-law violations) surfaces via the single-
                  // action 400 error path instead of being reduced to a count in
                  // BulkActionCard.
                  const PUBLISH_TOOLS = ["publish_schedule"];
                  const TICKET_TOOLS = ["create_ticket", "edit_ticket", "comment_on_ticket"];
                  const APPROVAL_TOOLS = ["decide_ticket_approval"];
                  const SUPPORT_TOOLS = ["create_support_request"];
                  const CUSTOM_CARD_TOOLS = [...MESSAGING_TOOLS, ...LOGBOOK_TOOLS, ...DAY_NOTE_TOOLS, ...TRAINING_INPUT_TOOLS, ...TRAINING_CHECKPOINT_TOOLS, ...FORM_GRADE_TOOLS, ...SAVE_REPORT_TOOLS, ...INVITE_TOOLS, ...ROSTER_TOOLS, ...ROLES_TOOLS, ...LOCATION_CREATE_TOOLS, ...LOCATION_SETTINGS_TOOLS, ...TIME_OFF_TOOLS, ...AVAILABILITY_TOOLS, ...PUBLISH_TOOLS, ...TICKET_TOOLS, ...APPROVAL_TOOLS, ...SUPPORT_TOOLS];
                  const hasCustomCard = message.proposals.some((p) => CUSTOM_CARD_TOOLS.includes(p.tool_name));
                  const hasNonCustom = message.proposals.some((p) => !CUSTOM_CARD_TOOLS.includes(p.tool_name));

                  if (hasCustomCard || !hasNonCustom) {
                    // Render each proposal with the appropriate card type
                    const nonMessagingProposals = message.proposals.filter((p) => !CUSTOM_CARD_TOOLS.includes(p.tool_name));
                    return (
                      <>
                        {message.proposals
                          .filter((p) => MESSAGING_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <MessageProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => DAY_NOTE_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <DayNoteProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => LOGBOOK_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <LogbookProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => TRAINING_INPUT_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <TrainingInputProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => TRAINING_CHECKPOINT_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <TrainingCheckpointProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => FORM_GRADE_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <FormGradeProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              homeCompany={homeCompany}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => SAVE_REPORT_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <SaveReportProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              currentChartWidgetType={getChartWidgetTypeForMessage(messageIndex)}
                              currentChartLayout={getChartLayoutForMessage(messageIndex)}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => INVITE_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <InviteProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              {...setupCardProps}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              // Setup mode is gated on company onboarding
                              // access, which requires a company_admin
                              // membership somewhere in the company, so the
                              // card must not inherit the current store's
                              // (possibly lower) role.
                              permissionRole={isSetupMode ? "company_admin" : permissionRole}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {isSetupMode &&
                          message.proposals
                            .filter((p) => LOCATION_SETTINGS_TOOLS.includes(p.tool_name))
                            .map((proposal) => (
                              <LocationSettingsProposalCard
                                key={proposal.proposal_id}
                                proposal={proposal}
                                {...setupCardProps}
                                sessionUuid={sessionUuid}
                                homeStore={sessionStoreId}
                                onStatusChange={(proposalId, newStatus) => {
                                  handleProposalStatusChange(message.id, proposalId, newStatus);
                                  if (newStatus === "confirmed") {
                                    notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                  }
                                }}
                              />
                            ))}
                        {isSetupMode &&
                          message.proposals
                            .filter((p) => ROSTER_TOOLS.includes(p.tool_name))
                            .map((proposal) => (
                              <RosterProposalCard
                                key={proposal.proposal_id}
                                proposal={proposal}
                                {...setupCardProps}
                                sessionUuid={sessionUuid}
                                homeStore={sessionStoreId}
                                onStatusChange={(proposalId, newStatus, details) => {
                                  handleProposalStatusChange(message.id, proposalId, newStatus);
                                  if (newStatus === "confirmed" || newStatus === "cancelled") {
                                    // The roster has been used (or declined); stop
                                    // sending it with every turn.
                                    setSetupRoster(null);
                                  }
                                  if (newStatus === "confirmed") {
                                    if (details?.send_welcome === false) {
                                      welcomesDeclinedRef.current = {
                                        storeId: proposal.store_id ?? proposal.tool_args?._store_id ?? null,
                                      };
                                    }
                                    notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                  }
                                }}
                              />
                            ))}
                        {isSetupMode &&
                          message.proposals
                            .filter((p) => LOCATION_CREATE_TOOLS.includes(p.tool_name))
                            .map((proposal) => (
                              <LocationCreateProposalCard
                                key={proposal.proposal_id}
                                proposal={proposal}
                                {...setupCardProps}
                                sessionUuid={sessionUuid}
                                homeStore={sessionStoreId}
                                onStatusChange={(proposalId, newStatus) => {
                                  handleProposalStatusChange(message.id, proposalId, newStatus);
                                  if (newStatus === "confirmed") {
                                    notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                  }
                                }}
                              />
                            ))}
                        {isSetupMode &&
                          message.proposals
                            .filter((p) => ROLES_TOOLS.includes(p.tool_name))
                            .map((proposal) => (
                              <RolesProposalCard
                                key={proposal.proposal_id}
                                proposal={proposal}
                                {...setupCardProps}
                                sessionUuid={sessionUuid}
                                homeStore={sessionStoreId}
                                onStatusChange={(proposalId, newStatus) => {
                                  handleProposalStatusChange(message.id, proposalId, newStatus);
                                  if (newStatus === "confirmed") {
                                    notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                  }
                                }}
                              />
                            ))}
                        {message.proposals
                          .filter((p) => TIME_OFF_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <TimeOffProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => AVAILABILITY_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <AvailabilityProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => PUBLISH_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <ActionCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              {...setupCardProps}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => TICKET_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <TicketProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              homeCompany={homeCompany}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => APPROVAL_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <TicketApprovalProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {message.proposals
                          .filter((p) => SUPPORT_TOOLS.includes(p.tool_name))
                          .map((proposal) => (
                            <SupportRequestProposalCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))}
                        {isSetupMode ? (
                          nonMessagingProposals.map((proposal) => (
                            <ActionCard
                              key={proposal.proposal_id}
                              proposal={proposal}
                              {...setupCardProps}
                              sessionUuid={sessionUuid}
                              homeStore={sessionStoreId}
                              onStatusChange={(proposalId, newStatus) => {
                                handleProposalStatusChange(message.id, proposalId, newStatus);
                                if (newStatus === "confirmed") {
                                  notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                                }
                              }}
                            />
                          ))
                        ) : nonMessagingProposals.length === 1 ? (
                          <ActionCard
                            proposal={nonMessagingProposals[0]}
                            sessionUuid={sessionUuid}
                            homeStore={sessionStoreId}
                            onStatusChange={(proposalId, newStatus) => {
                              handleProposalStatusChange(message.id, proposalId, newStatus);
                              if (newStatus === "confirmed") {
                                notifyActionConfirmed([nonMessagingProposals[0].tool_name]);
                              }
                            }}
                          />
                        ) : nonMessagingProposals.length > 1 ? (
                          <BulkActionCard
                            proposals={nonMessagingProposals}
                            sessionUuid={sessionUuid}
                            homeStore={sessionStoreId}
                            onStatusChange={(proposalIds, newStatus) => {
                              handleProposalStatusChange(message.id, proposalIds, newStatus);
                              if (newStatus === "confirmed") {
                                const ids = Array.isArray(proposalIds) ? proposalIds : [proposalIds];
                                const toolNames = [...new Set(
                                  nonMessagingProposals
                                    .filter((p) => ids.includes(p.proposal_id))
                                    .map((p) => p.tool_name)
                                )];
                                notifyActionConfirmed(toolNames);
                              }
                            }}
                          />
                        ) : null}
                      </>
                    );
                  } else if (isSetupMode && message.proposals.length > 1) {
                    // Several plain cards in one turn (resends, job mappings,
                    // location imports): one card with a checkbox per item.
                    const toolNames = [...new Set(message.proposals.map((p) => p.tool_name))];
                    return (
                      <SetupBulkActionCard
                        proposals={message.proposals}
                        {...setupCardProps}
                        sessionUuid={sessionUuid}
                        homeStore={sessionStoreId}
                        onStatusChange={(proposalIds, newStatus) => handleProposalStatusChange(message.id, proposalIds, newStatus)}
                        onConfirmed={(proposalIds, resolvedIds) => {
                          const title = message.proposals[0]?.summary?.title || toolNames[0];
                          notifyActionConfirmed(toolNames, `${title} (${proposalIds.length})`, resolvedIds || proposalIds);
                        }}
                      />
                    );
                  } else if (isSetupMode) {
                    return message.proposals.map((proposal) => (
                      <ActionCard
                        key={proposal.proposal_id}
                        proposal={proposal}
                        {...setupCardProps}
                        sessionUuid={sessionUuid}
                        homeStore={sessionStoreId}
                        onStatusChange={(proposalId, newStatus) => {
                          handleProposalStatusChange(message.id, proposalId, newStatus);
                          if (newStatus === "confirmed") {
                            notifyActionConfirmed([proposal.tool_name], proposal.summary?.title || proposal.tool_name, proposal.proposal_id);
                          }
                        }}
                      />
                    ));
                  } else if (message.proposals.length === 1) {
                    return (
                      <ActionCard
                        proposal={message.proposals[0]}
                        sessionUuid={sessionUuid}
                        homeStore={sessionStoreId}
                        onStatusChange={(proposalId, newStatus) => {
                          handleProposalStatusChange(message.id, proposalId, newStatus);
                          if (newStatus === "confirmed") {
                            notifyActionConfirmed([message.proposals[0].tool_name]);
                          }
                        }}
                      />
                    );
                  } else {
                    return (
                      <BulkActionCard
                        proposals={message.proposals}
                        sessionUuid={sessionUuid}
                        homeStore={sessionStoreId}
                        onStatusChange={(proposalIds, newStatus) => {
                          handleProposalStatusChange(message.id, proposalIds, newStatus);
                          if (newStatus === "confirmed") {
                            const ids = Array.isArray(proposalIds) ? proposalIds : [proposalIds];
                            const toolNames = [...new Set(
                              message.proposals
                                .filter((p) => ids.includes(p.proposal_id))
                                .map((p) => p.tool_name)
                            )];
                            notifyActionConfirmed(toolNames);
                          }
                        }}
                      />
                    );
                  }
                })()}

              <span
                className={`text-xs block mt-1 ${
                  message.sender === "user" ? "text-gray-500 text-right" : "text-gray-400"
                }`}
              >
                {formatTimestamp(message.timestamp)}
              </span>
            </div>
          </div>
        ))}

        {/* Suggestion chips — badge-driven contextual first, then user shortcuts, then Manage */}
        {!isStreaming && (messages[messages.length - 1]?.showSuggestions || setupChipsAvailable) && getSuggestions().length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {getSuggestions().map((s, i) => (
              <button
                key={s.shortcutId ? `sc-${s.shortcutId}` : `badge-${i}`}
                onClick={() => handleSuggestionClick(s)}
                title={isSetupMode ? undefined : s.prompt}
                className={
                  s.kind === "answer"
                    ? "px-3 py-1.5 text-xs rounded-full border border-violet-400 text-violet-800 bg-violet-50 hover:bg-violet-100 transition-colors"
                    : s.kind === "done"
                    ? "px-3 py-1.5 text-xs rounded-full border border-green-400 text-green-800 bg-green-50 hover:bg-green-100 transition-colors"
                    : s.kind === "skip" || s.kind === "back" || s.kind === "decline"
                    ? "px-3 py-1.5 text-xs rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    : "px-3 py-1.5 text-xs rounded-full border border-violet-300 text-violet-700 bg-white hover:bg-violet-50 transition-colors"
                }
              >
                {s.label}
              </button>
            ))}
            {!isSetupMode && (
            <button
              onClick={() => setIsManageOpen(true)}
              title="Manage shortcuts"
              className="px-3 py-1.5 text-xs rounded-full border border-dashed border-violet-300 text-violet-700 bg-white hover:bg-violet-50 transition-colors flex items-center gap-1"
            >
              <TuneIcon style={{ fontSize: 14 }} />
              {shortcuts.length === 0 ? "Add shortcuts" : "Manage"}
            </button>
            )}
          </div>
        )}

        {/* Thinking indicator */}
        {isStreaming && thinkingText && (
          <div className="flex items-center mb-3">
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <RotateRightIcon
                fontSize="small"
                className="text-violet-700 animate-spin"
              />
              <span className="text-gray-500 text-sm">{thinkingText}</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
       </div>
      </div>

      {/* Input area */}
      <div className="border-t bg-white p-2 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              voiceState === "recording"
                ? "Listening..."
                : voiceState === "transcribing"
                  ? "Transcribing..."
                  : isSetupMode
                    ? `Ask ${SETUP_ASSISTANT_NAME} about setup...`
                    : "Ask about your store..."
            }
            disabled={voiceState !== "idle"}
            className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-700 outline-none resize-none disabled:opacity-60"
            rows="1"
            style={{ maxHeight: 100 }}
          />

          {/* Cancel-recording button — visible only while recording so the
              manager can bail out without billing for a transcript. */}
          {voiceState === "recording" && (
            <button
              onClick={cancelRecording}
              className="rounded-full w-8 h-8 flex items-center justify-center shrink-0 bg-gray-200 text-gray-600"
              title="Cancel recording"
            >
              <CloseIcon fontSize="small" />
            </button>
          )}

          {/* Mic / send: while the textarea has text we show send; otherwise
              the mic button. The mic itself toggles between idle and recording.
              Transcribing shows a spinner inside the same circle. */}
          {isSetupMode || (inputValue.trim() && voiceState === "idle") ? (
            <button
              onClick={handleSendMessage}
              disabled={isStreaming || !inputValue.trim()}
              className={`rounded-full w-8 h-8 flex items-center justify-center shrink-0 ${
                isStreaming || (isSetupMode && !inputValue.trim())
                  ? "bg-gray-200 text-gray-400"
                  : "bg-violet-700 text-white"
              }`}
            >
              <SendIcon fontSize="small" />
            </button>
          ) : (
            <button
              onClick={() => {
                if (voiceState === "idle") startRecording();
                else if (voiceState === "recording")
                  stopRecordingAndTranscribe();
              }}
              disabled={isStreaming || voiceState === "transcribing"}
              title={
                voiceState === "recording"
                  ? "Stop and transcribe"
                  : "Record voice input"
              }
              className={`rounded-full w-8 h-8 flex items-center justify-center shrink-0 ${
                voiceState === "recording"
                  ? "bg-red-600 text-white animate-pulse"
                  : isStreaming || voiceState === "transcribing"
                    ? "bg-gray-200 text-gray-400"
                    : "bg-violet-700 text-white"
              }`}
            >
              {voiceState === "transcribing" ? (
                <RotateRightIcon
                  fontSize="small"
                  className="animate-spin"
                />
              ) : voiceState === "recording" ? (
                <StopIcon fontSize="small" />
              ) : (
                <MicIcon fontSize="small" />
              )}
            </button>
          )}
        </div>
      </div>

      {!isSetupMode && (
      <ManageShortcutsModal
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        shortcuts={shortcuts}
        setShortcuts={setShortcuts}
        templates={shortcutTemplates}
      />
      )}
    </div>
  );
}
